import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/AdminCalendar.css";
import { NavbarAdmin } from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import Toast from "../components/Toast";
import API from "../config";

const getAuthHeaders = (json = false) => {
  const token = localStorage.getItem("token");
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
const STATUS_CONFIG = {
  Confirmed:   { label: "จองห้องแล้ว",        color: "#065f46", bg: "#d1fae5", border: "#10b981" },
  Approved:    { label: "จองห้องแล้ว",        color: "#065f46", bg: "#d1fae5", border: "#10b981" },
  Pending:     { label: "จองห้องแล้ว",        color: "#065f46", bg: "#d1fae5", border: "#10b981" },
  CheckedIn:   { label: "ลูกค้าเข้าพักอยู่",  color: "#1e3a8a", bg: "#dbeafe", border: "#3b82f6" },
  Cancelled:   { label: "ยกเลิกการจอง",       color: "#991b1b", bg: "#fef2f2", border: "#fca5a5" },
  Maintenance: { label: "กำลังซ่อมแซม",       color: "#7f1d1d", bg: "#fee2e2", border: "#ef4444" },
};

/* ============================================================
   PRESET COLORS FOR BLOCK
   ============================================================ */
const BLOCK_TYPES = [
  { key: "Occupied",    label: "ห้องพักไม่ว่าง",  icon: "🚫", color: "#7f1d1d", bg: "#fee2e2", border: "#ef4444" },
  { key: "Maintenance", label: "กำลังซ่อมแซม",    icon: "🔧", color: "#7f1d1d", bg: "#fee2e2", border: "#ef4444" },
  { key: "Other",       label: "อื่นๆ",            icon: "📌", color: "#374151", bg: "#f3f4f6", border: "#9ca3af" },
];
const PALETTE = [
  "#ef4444","#f97316","#eab308","#22c55e","#06b6d4",
  "#3b82f6","#8b5cf6","#ec4899","#6b7280","#1a237e",
];

const ZOOM_LEVELS  = [28, 36, 48, 64, 80];
const ZOOM_DEFAULT = 1;

/* สีประจำประเภทห้อง (key = Room.type) — ใช้กับแถบสีหน้าเลขห้อง + จุดสีใน filter (โทนเดียวกับ badge หน้า AdminRooms) */
const TYPE_COLORS = {
  Single: "#0369a1",
  Double: "#166534",
  Family: "#9a3412",
  Triple: "#6b21a8",
  Quad:   "#9d174d",
};
/* ชื่อย่อประเภทห้องใต้เลขห้อง (i18n key) */
const TYPE_SHORT_KEYS = {
  Single: "calTypeSingle",
  Double: "calTypeDouble",
  Family: "calTypeFamily",
  Triple: "calTypeTriple",
  Quad:   "calTypeQuad",
};


const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString("th-TH", { year:"numeric", month:"long", day:"numeric" })
  : "-";

/* "ศ. 7 ส.ค. 2569" — ใช้บนแถบเลือกหลายห้อง ให้ปีเป็น พ.ศ. ตรงกับ dropdown ปีด้านบน
   (T00:00:00 ไม่ใส่ Z เพราะอยากได้วันตามเวลาเครื่อง ไม่ใช่ UTC) */
const fmtBarDate = (s, lang) => s
  ? new Date(`${s}T00:00:00`).toLocaleDateString(lang === "th" ? "th-TH" : "en-GB",
      { weekday: "short", day: "numeric", month: "short", year: "numeric" })
  : "—";

/* "YYYY-MM-DD" + n วัน -> "YYYY-MM-DD" (คำนวณแบบ UTC กัน timezone เลื่อนวัน) */
const addDaysStr = (dateStr, n) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const nightsBetween = (from, to) =>
  Math.round((new Date(`${to}T00:00:00Z`) - new Date(`${from}T00:00:00Z`)) / 86400000);

const ymd = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/* วันนี้แบบ local (ไม่ใช้ toISOString ตรงๆ เพราะจะเลื่อนเป็นวันก่อนหน้าในโซนเวลา +07) */
const todayStr = () => {
  const d = new Date();
  return ymd(d.getFullYear(), d.getMonth(), d.getDate());
};

/* ============================================================
   DATE FIELD
   input[type=date] ของเบราว์เซอร์โชว์ MM/DD/YYYY ค.ศ. ตาม locale ของเครื่อง
   ซึ่งอ่านสลับกับที่อื่นในหน้านี้ (08/07 กับ 08/13 คือเดือน/วัน ไม่ใช่ วัน/เดือน)
   จึงวาดข้อความไทยทับ แล้วเก็บ input จริงไว้ใต้ปุ่มเพื่อเรียก picker ตัวเดิม
   ============================================================ */
function DateField({ value, min, onChange, lang }) {
  const ref = useRef(null);
  /* showPicker ต้องมี user activation — เรียกจาก event handler ตรงนี้เท่านั้น
     เบราว์เซอร์เก่าไม่มีเมธอดนี้ ตกไปที่ focus ให้พิมพ์เองได้ */
  const open = () => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.focus();
  };
  /* ใช้ span ไม่ใช่ button — button ห้ามมี input ข้างใน (interactive content ซ้อนกัน) */
  return (
    <span className="cal-date-field" role="button" tabIndex={0}
      onClick={open}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}>
      <span className="cal-date-field-text">{fmtBarDate(value, lang)}</span>
      <span className="cal-date-field-ico" aria-hidden="true">📅</span>
      {/* ยัง render จริง (แค่โปร่งใส) เพราะ showPicker โยน error ถ้า element ไม่ถูกวาด */}
      <input ref={ref} type="date" value={value} min={min} onChange={onChange} tabIndex={-1} />
    </span>
  );
}

/* ============================================================
   BOOKING DATE-RANGE PICKER
   เลือกช่วงวันในปฏิทินเดียว + ปิดคืนที่ห้องไม่ว่าง
   ============================================================ */
function BookingDatePicker({ valueIn, valueOut, onChange, isNightFree, lang }) {
  const seed = valueIn ? new Date(`${valueIn}T00:00:00Z`) : new Date();
  const [vy, setVy] = useState(seed.getUTCFullYear());
  const [vm, setVm] = useState(seed.getUTCMonth()); // 0-11

  const months  = t(lang, "calMonthsTH");
  const beOffset = lang === "th" ? 543 : 0;
  const DOW = lang === "th"
    ? ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"]
    : ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const daysInMonth = new Date(Date.UTC(vy, vm + 1, 0)).getUTCDate();
  const firstDow    = new Date(Date.UTC(vy, vm, 1)).getUTCDay();
  const todayStr    = new Date().toISOString().slice(0, 10);

  const prevMonth = () => { const d = new Date(Date.UTC(vy, vm - 1, 1)); setVy(d.getUTCFullYear()); setVm(d.getUTCMonth()); };
  const nextMonth = () => { const d = new Date(Date.UTC(vy, vm + 1, 1)); setVy(d.getUTCFullYear()); setVm(d.getUTCMonth()); };

  /* คืน [from, to) ทุกคืนว่างหรือไม่ */
  const rangeFree = (from, to) => {
    for (let d = from; d < to; d = addDaysStr(d, 1)) {
      if (!isNightFree(d)) return false;
    }
    return true;
  };

  const handleDay = (dateStr, occupied) => {
    if (occupied) return;
    // ยังไม่เลือก / เลือกครบแล้ว / คลิกก่อนวันเข้า -> เริ่มใหม่ที่วันนี้เป็นวันเข้า
    if (!valueIn || (valueIn && valueOut) || dateStr <= valueIn) {
      onChange(dateStr, "");
      return;
    }
    // valueIn มีแล้ว ยังไม่มีวันออก, dateStr > valueIn -> ปิดช่วง ถ้าทุกคืนว่าง
    if (rangeFree(valueIn, dateStr)) onChange(valueIn, dateStr);
    else onChange(dateStr, "");
  };

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button type="button" onClick={prevMonth}
          style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: "#64748b", lineHeight: 1, padding: "2px 8px" }}>‹</button>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{months[vm]} {vy + beOffset}</span>
        <button type="button" onClick={nextMonth}
          style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: "#64748b", lineHeight: 1, padding: "2px 8px" }}>›</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, textAlign: "center", fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>
        {DOW.map((d, i) => <div key={i} style={{ color: (i === 0 || i === 6) ? "#e24b4a" : "#94a3b8" }}>{d}</div>)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, textAlign: "center", fontSize: 13 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={`p${i}`} />;
          const dateStr  = ymd(vy, vm, d);
          const occupied = !isNightFree(dateStr);
          const isIn     = dateStr === valueIn;
          const isOut    = dateStr === valueOut;
          const inRange  = valueIn && valueOut && dateStr > valueIn && dateStr < valueOut;
          const isPast   = dateStr < todayStr;

          let bg = "transparent", color = "#1e293b", radius = 8, weight = 400;
          if (isIn || isOut)      { bg = "#10b981"; color = "#fff"; weight = 500; }
          else if (inRange)       { bg = "#d1fae5"; color = "#04342C"; }
          if (isIn && valueOut)   radius = "8px 0 0 8px";
          if (isOut)              radius = "0 8px 8px 0";
          if (occupied)           { color = "#cbd5e1"; }
          else if (isPast && !isIn && !isOut && !inRange) { color = "#cbd5e1"; }

          return (
            <div key={dateStr} onClick={() => handleDay(dateStr, occupied)}
              style={{
                padding: "6px 0", borderRadius: radius, background: bg, color, fontWeight: weight,
                cursor: occupied ? "not-allowed" : "pointer",
                textDecoration: occupied ? "line-through" : "none",
              }}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   CELL ACTION POPUP
   ============================================================ */
function CellActionPopup({ info, onClose, onSave, onCreateBooking, onDelete, editBlock, isUnitFree, lang }) {
  const [mode,     setMode]     = useState("booking"); // "booking" | "block"
  const [type,     setType]     = useState(BLOCK_TYPES[0].key);
  const [color,    setColor]    = useState(BLOCK_TYPES[0].border);
  const [note,     setNote]     = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [saving,   setSaving]   = useState(false);

  /* ---- booking form ---- */
  const [bkIn,      setBkIn]      = useState("");
  const [bkOut,     setBkOut]     = useState("");
  const [guestName, setGuestName] = useState("");
  const [phone,     setPhone]     = useState("");
  const [paid,       setPaid]       = useState("full");  // "full" | "partial" | "none"
  const [partialAmt, setPartialAmt] = useState("");
  const [checkInNow, setCheckInNow] = useState(false);
  const [bkErr,      setBkErr]      = useState("");

  /* ห้องที่ยังติ๊กอยู่ในป็อปอัป (ปลดออกได้ที่นี่โดยไม่ต้องกลับไปแก้บนปฏิทิน) */
  const [pickedIds, setPickedIds] = useState([]);

  /* ชื่อผู้เข้าพัก: ใช้ชื่อเดียวทุกห้อง (ค่าเริ่มต้น) หรือแยกชื่อรายห้อง — 1 ห้อง = 1 ชื่อ */
  const [sameGuest,  setSameGuest]  = useState(true);
  const [roomGuests, setRoomGuests] = useState({});   // { [unitId]: { name, phone } }

  const setRoomGuest = (id, field, val) =>
    setRoomGuests(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: val } }));

  useEffect(() => {
    if (editBlock) {
      setMode("block");
      setType(editBlock.type);
      setColor(editBlock.color);
      setNote(editBlock.note || "");
      setDateFrom(editBlock.dateFrom?.slice(0, 10) || "");
      setDateTo(editBlock.dateTo?.slice(0, 10)     || "");
    } else if (info) {
      const list = info.units || (info.unit ? [info.unit] : []);
      setMode("booking");
      setType(BLOCK_TYPES[0].key);
      setColor(BLOCK_TYPES[0].border);
      setNote("");
      setDateFrom(info.date);
      setDateTo(info.date);
      // มาจากโหมดเลือกหลายห้องจะมีช่วงวันติดมาแล้ว — คลิกเซลล์เดี่ยวใช้วันของเซลล์นั้น
      setBkIn(info.checkIn  || info.date);
      setBkOut(info.checkOut || addDaysStr(info.date, 1));
      setPickedIds(list.map(u => u._id?.toString()));
      setSameGuest(true); setRoomGuests({});
      setGuestName(""); setPhone("");
      setPaid("full"); setPartialAmt("");
      setCheckInNow(false);
      setBkErr("");
    }
  }, [info, editBlock]);

  const popupUnits = info?.units || (info?.unit ? [info.unit] : []);

  /* ช่วงวันเปลี่ยน → ห้องที่ชนถูกปลดติ๊กอัตโนมัติ จะได้ไม่ส่งไปให้ backend ปฏิเสธ */
  useEffect(() => {
    if (!info || !bkIn || !bkOut || !isUnitFree) return;
    setPickedIds(prev => {
      const kept = prev.filter(id => {
        const u = popupUnits.find(x => x._id?.toString() === id);
        return u && isUnitFree(u._id, bkIn, bkOut);
      });
      return kept.length === prev.length ? prev : kept;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bkIn, bkOut, info]);

  if (!info && !editBlock) return null;

  const isEdit = !!editBlock;
  const roomNumber = editBlock?.roomNumber || info?.roomNumber;
  const subtitle   = editBlock ? `${editBlock.dateFrom} → ${editBlock.dateTo}` : info?.date;

  const isBooking   = mode === "booking" && !isEdit;
  const unit        = info?.unit;
  const bkNights    = (bkIn && bkOut) ? nightsBetween(bkIn, bkOut) : 0;
  const BOOK_COLOR  = STATUS_CONFIG.Approved.border;

  const isMulti      = popupUnits.length > 1;
  const pickedUnits  = popupUnits.filter(u => pickedIds.includes(u._id?.toString()));

  /* ช่วงวันของห้องนั้น — จองหลายห้องแต่ละห้องเข้า-ออกคนละวันได้ (ตั้งมาจากปฏิทิน)
     จองห้องเดียวยังใช้ bkIn/bkOut ที่แก้ได้ในป็อปอัปเหมือนเดิม */
  const rangeOfUnit = (u) => (isMulti && info?.ranges?.[u._id?.toString()])
    || { in: bkIn, out: bkOut };
  const nightsOfUnit = (u) => {
    const r = rangeOfUnit(u);
    return (r.in && r.out) ? Math.max(0, nightsBetween(r.in, r.out)) : 0;
  };

  /* ราคาบวกทีละห้อง — เลือกข้ามประเภทได้ ราคาต่อคืนและจำนวนคืนไม่เท่ากัน */
  const bkTotal = pickedUnits.reduce(
    (sum, u) => sum + (Number(u.roomType?.price) || 0) * nightsOfUnit(u), 0);
  const unitFreeNow = (u) => {
    const r = rangeOfUnit(u);
    return !isUnitFree || isUnitFree(u._id, r.in, r.out);
  };
  /* จองห้องเดียวไม่ต้องมีตัวเลือกแยกชื่อ — บังคับเป็นชื่อเดียวเสมอ */
  const useSameGuest = !isMulti || sameGuest;

  const bkPaidAmount = paid === "full"    ? bkTotal
                     : paid === "partial" ? (Number(partialAmt) || 0)
                     : 0;
  const bkRemaining  = Math.max(0, bkTotal - bkPaidAmount);

  const handleBookingSave = async () => {
    if (saving) return;
    if (!pickedUnits.length) { setBkErr(t(lang, "calBkErrNoRoom")); return; }
    if (pickedUnits.some(u => nightsOfUnit(u) < 1)) { setBkErr(t(lang, "calBkErrDate")); return; }
    if (useSameGuest) {
      if (!guestName.trim()) { setBkErr(t(lang, "calBkErrName")); return; }
    } else {
      const missing = pickedUnits.filter(u => !(roomGuests[u._id?.toString()]?.name || "").trim());
      if (missing.length) {
        setBkErr(`${t(lang, "calBkErrNamePerRoom")} (${missing.map(u => u.roomNumber).join(", ")})`);
        return;
      }
    }
    if (paid === "partial" && (bkPaidAmount <= 0 || bkPaidAmount > bkTotal)) {
      setBkErr(t(lang, "calBkErrPartial")); return;
    }
    const busy = pickedUnits.filter(u => !unitFreeNow(u));
    if (busy.length) {
      setBkErr(`${t(lang, "calBkErrOverlap")} (${busy.map(u => u.roomNumber).join(", ")})`);
      return;
    }
    setBkErr("");
    setSaving(true);
    const result = await onCreateBooking({
      units: pickedUnits, checkIn: bkIn, checkOut: bkOut,
      /* ชื่อรายห้อง — โหมด "ชื่อเดียวกัน" ส่งช่องว่างไป backend จะตกไปใช้ guestName ของกลุ่มเอง */
      rooms: pickedUnits.map(u => {
        const g = roomGuests[u._id?.toString()] || {};
        const r = rangeOfUnit(u);
        return {
          unitId:    u._id,
          checkIn:   r.in,
          checkOut:  r.out,
          guestName: useSameGuest ? "" : (g.name  || "").trim(),
          phone:     useSameGuest ? "" : (g.phone || "").trim(),
        };
      }),
      guestName: useSameGuest ? guestName.trim() : "",
      phone:     useSameGuest ? phone.trim()     : "",
      paidAmount: bkPaidAmount,
      checkInNow,
    });
    setSaving(false);
    if (result?.ok) onClose();
    else setBkErr(result?.message || t(lang, "calToastSaveFail"));
  };

  const handleType = (k) => {
    setType(k);
    const preset = BLOCK_TYPES.find(b => b.key === k);
    if (preset) setColor(preset.border);
  };

  const chosen = BLOCK_TYPES.find(b => b.key === type) || BLOCK_TYPES[0];
  const colorLocked = type === "Occupied" || type === "Maintenance";

  const handleSave = async () => {
    if (saving) return;
    if (type === "Other" && !note.trim()) return;
    setSaving(true);
    const unitId = editBlock?.unitId || info?.unitId;
    await onSave({ _id: editBlock?._id, unitId, type, color, note, dateFrom, dateTo });
    setSaving(false);
    onClose();
  };

  const handleDeleteClick = () => {
    onDelete(editBlock);
    onClose();
  };

  return (
    /* คลิกพื้นหลังไม่ปิดป็อปอัป — ฟอร์มนี้กรอกหลายช่อง เผลอคลิกข้างๆ แล้วข้อมูลหายทั้งใบ
       ต้องกดปุ่มยกเลิก หรือ ✕ เท่านั้น */
    <div className="bm-overlay">
      <div className="cap-card">

        <div className="cap-header" style={{ borderBottom: `3px solid ${isBooking ? BOOK_COLOR : color}` }}>
          <div className="cap-header-left">
            <span className="cap-room-badge">
              {isMulti ? `${popupUnits.length} ${t(lang, "calRoomsUnit")}` : roomNumber}
            </span>
            <div>
              <div className="cap-title">
                {isEdit ? t(lang, "calEditPeriod")
                  : isBooking ? t(lang, "calTabBooking")
                  : t(lang, "calManagePeriod")}
              </div>
              <div className="cap-subtitle">
                {isMulti
                  ? popupUnits.map(u => u.roomNumber).join(", ")
                  : (isBooking && unit?.roomType?.name ? `${unit.roomType.name} · ${subtitle}` : subtitle)}
              </div>
            </div>
          </div>
          <button className="bm-close" onClick={onClose}>✕</button>
        </div>

        <div className="cap-body">
          {/* แท็บเลือกโหมด — ซ่อนตอนแก้ไขบล็อกเดิม และตอนจองหลายห้อง (บล็อกทีละหลายห้องไม่รองรับ) */}
          {!isEdit && !isMulti && (
            <div className="cap-type-row" style={{ marginBottom: 4 }}>
              {[["booking", "🛏️", "calTabBooking", BOOK_COLOR, "#d1fae5", "#065f46"],
                ["block",   "🚫", "calTabBlock",   "#ef4444",  "#fee2e2", "#7f1d1d"]].map(
                ([key, icon, labelKey, border, bg, fg]) => (
                  <button key={key}
                    className={`cap-type-btn ${mode === key ? "active" : ""}`}
                    style={mode === key ? { background: bg, borderColor: border, color: fg } : {}}
                    onClick={() => setMode(key)}>
                    <span>{icon}</span>
                    <span>{t(lang, labelKey)}</span>
                  </button>
                ))}
            </div>
          )}

          {isBooking ? (
            <>
              {/* จองหลายห้อง: แต่ละห้องมีช่วงวันของตัวเอง (ตั้งมาจากปฏิทิน) จึงไม่โชว์ปฏิทินรวม
                  ถ้าจะแก้วันให้กลับไปลากบนปฏิทิน — ที่นี่โชว์วันของแต่ละห้องในรายการด้านล่างแทน */}
              {!isMulti && (
                <>
                  <div className="cap-date-row">
                    <div className="cap-field" style={{ flex: 1 }}>
                      <label className="cap-section-label">{t(lang, "calStartDate")}</label>
                      <div style={{
                        padding: "8px 12px", borderRadius: 8, fontSize: 14, fontWeight: 600,
                        border: `1px solid ${bkIn ? BOOK_COLOR : "#e5e7eb"}`,
                        background: bkIn ? "#d1fae5" : "#f9fafb", color: bkIn ? "#04342C" : "#94a3b8",
                      }}>{bkIn ? fmtDate(bkIn) : "—"}</div>
                    </div>
                    <div className="cap-arrow">→</div>
                    <div className="cap-field" style={{ flex: 1 }}>
                      <label className="cap-section-label">{t(lang, "calEndDate")}</label>
                      <div style={{
                        padding: "8px 12px", borderRadius: 8, fontSize: 14, fontWeight: 600,
                        border: `1px solid ${bkOut ? BOOK_COLOR : "#e5e7eb"}`,
                        background: bkOut ? "#d1fae5" : "#f9fafb", color: bkOut ? "#04342C" : "#94a3b8",
                      }}>{bkOut ? fmtDate(bkOut) : "—"}</div>
                    </div>
                  </div>

                  <BookingDatePicker
                    valueIn={bkIn} valueOut={bkOut}
                    onChange={(ci, co) => { setBkIn(ci); setBkOut(co); setBkErr(""); }}
                    isNightFree={(d) => !isUnitFree
                      || popupUnits.some(u => isUnitFree(u._id, d, addDaysStr(d, 1)))}
                    lang={lang}
                  />
                </>
              )}

              {/* รายการห้องที่เลือก — อ่านอย่างเดียว จะเพิ่ม/เอาห้องออกต้องกลับไปแก้บนปฏิทิน */}
              {isMulti && (
                <>
                  <div className="cap-section-label" style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{t(lang, "calBkRoomList")}</span>
                    <span style={{ color: pickedUnits.length ? BOOK_COLOR : "#ef4444", fontWeight: 700 }}>
                      {pickedUnits.length}/{popupUnits.length}
                    </span>
                  </div>
                  <div className="cap-room-list">
                    {popupUnits.map(u => {
                      const id    = u._id?.toString();
                      const free  = unitFreeNow(u);
                      const on    = pickedIds.includes(id);
                      const rate  = Number(u.roomType?.price) || 0;
                      return (
                        <div key={id}
                          className={`cap-room-item${on ? " picked" : ""}${free ? "" : " busy"}`}
                          title={free ? "" : t(lang, "calBkRoomBusy")}>
                          <span className="cap-room-no">{u.roomNumber}</span>
                          <span className="cap-room-type">
                            {u.roomType?.name || "—"}
                            {/* แต่ละห้องเข้า-ออกคนละวันได้ จึงต้องบอกช่วงวันของห้องนี้ */}
                            <em className="cap-room-dates">
                              {rangeOfUnit(u).in?.slice(5)} → {rangeOfUnit(u).out?.slice(5)} · {nightsOfUnit(u)} {t(lang, "calNightsUnit")}
                            </em>
                          </span>
                          <span className="cap-room-rate">
                            {free
                              ? `฿${rate.toLocaleString("th-TH")} × ${nightsOfUnit(u)} = ฿${(rate * nightsOfUnit(u)).toLocaleString("th-TH")}`
                              : `⚠️ ${t(lang, "calBkRoomBusy")}`}
                          </span>

                          {/* แยกชื่อรายห้อง — 1 ห้อง = 1 ชื่อ */}
                          {!useSameGuest && on && free && (
                            <div className="cap-room-guest">
                              {/* กรอกไม่ครบแล้วกดยืนยัน — ขึ้นกรอบแดงชี้ห้องที่ยังขาดชื่อ */}
                              <input type="text" required
                                className={`cap-input cap-room-guest-name${
                                  bkErr && !(roomGuests[id]?.name || "").trim() ? " err" : ""}`}
                                value={roomGuests[id]?.name || ""}
                                placeholder={t(lang, "calBkGuestNamePh")}
                                onChange={e => { setRoomGuest(id, "name", e.target.value); setBkErr(""); }} />
                              <input type="tel" className="cap-input cap-room-guest-phone"
                                value={roomGuests[id]?.phone || ""}
                                placeholder={t(lang, "calBkPhonePh")}
                                onChange={e => setRoomGuest(id, "phone", e.target.value)} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* สลับระหว่างชื่อเดียวทั้งกลุ่ม กับแยกชื่อรายห้อง */}
              {isMulti && (
                <label className="cap-same-guest">
                  <input type="checkbox" checked={sameGuest}
                    onChange={e => { setSameGuest(e.target.checked); setBkErr(""); }} />
                  <div>
                    <div className="cap-same-guest-title">{t(lang, "calBkSameGuest")}</div>
                    <div className="cap-same-guest-hint">
                      {sameGuest ? t(lang, "calBkSameGuestOn") : t(lang, "calBkSameGuestOff")}
                    </div>
                  </div>
                </label>
              )}

              {useSameGuest && (
                <>
                  <div className="cap-section-label">
                    {t(lang, "calBkGuestName")} <span style={{ color: "#ef4444" }}>*</span>
                  </div>
                  <input type="text" className="cap-input" value={guestName}
                    placeholder={t(lang, "calBkGuestNamePh")}
                    onChange={e => { setGuestName(e.target.value); setBkErr(""); }} />

                  <div className="cap-section-label">
                    {t(lang, "calBkPhone")}{" "}
                    <span style={{ color: "#9ca3af", fontWeight: 400 }}>({t(lang, "calBkPhoneOpt")})</span>
                  </div>
                  <input type="tel" className="cap-input" value={phone}
                    placeholder={t(lang, "calBkPhonePh")}
                    onChange={e => setPhone(e.target.value)} />
                </>
              )}

              <div className="cap-section-label">{t(lang, "calBkPayment")}</div>
              <div className="cap-type-row">
                {[["full", "calBkPaidFull"], ["partial", "calBkPaidPartial"], ["none", "calBkPaidNone"]].map(([k, lk]) => (
                  <button key={k}
                    className={`cap-type-btn ${paid === k ? "active" : ""}`}
                    style={paid === k ? { background: "#d1fae5", borderColor: BOOK_COLOR, color: "#065f46" } : {}}
                    onClick={() => setPaid(k)}>
                    <span>{t(lang, lk)}</span>
                  </button>
                ))}
              </div>

              {paid === "partial" && (
                <>
                  <div className="cap-section-label">{t(lang, "calBkPartialAmt")}</div>
                  <input type="number" className="cap-input" value={partialAmt}
                    min={0} max={bkTotal} step={1} placeholder="0"
                    onChange={e => setPartialAmt(e.target.value)} />
                </>
              )}

              <label style={{
                display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                padding: "10px 12px", marginTop: 4, borderRadius: 10,
                border: `1px solid ${checkInNow ? BOOK_COLOR : "#e5e7eb"}`,
                background: checkInNow ? "#d1fae5" : "#f9fafb",
              }}>
                <input type="checkbox" checked={checkInNow}
                  onChange={e => setCheckInNow(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: BOOK_COLOR, cursor: "pointer" }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: checkInNow ? "#065f46" : "#374151" }}>
                    {t(lang, "calBkCheckInNow")}
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{t(lang, "calBkCheckInHint")}</div>
                </div>
              </label>

              <div className="cap-preview" style={{ background: BOOK_COLOR + "22", borderLeft: `4px solid ${BOOK_COLOR}` }}>
                <span style={{ color: "#065f46" }}>
                  {t(lang, "calBkTotal")} ฿{bkTotal.toLocaleString("th-TH")}
                </span>
                <span className="cap-preview-dates" style={{ color: bkRemaining > 0 ? "#b45309" : "#065f46" }}>
                  {bkRemaining > 0
                    ? `${t(lang, "calBkRemain")} ฿${bkRemaining.toLocaleString("th-TH")}`
                    : `${pickedUnits.length} ${t(lang, "calRoomsUnit")} · ${Math.max(0, bkNights)} ${t(lang, "calNightsUnit")}`}
                </span>
              </div>

              {bkErr && (
                <div style={{ fontSize: 12, color: "#ef4444", marginTop: 2 }}>⚠️ {bkErr}</div>
              )}
            </>
          ) : (
          <>
          <div className="cap-section-label">{t(lang, "calType")}</div>
          <div className="cap-type-row">
            {BLOCK_TYPES.map(b => (
              <button key={b.key}
                className={`cap-type-btn ${type === b.key ? "active" : ""}`}
                style={type === b.key ? { background: b.bg, borderColor: b.border, color: b.color } : {}}
                onClick={() => handleType(b.key)}>
                <span>{b.icon}</span>
                <span>{b.key === "Occupied" ? t(lang, "calOccupied") : b.key === "Maintenance" ? t(lang, "calMaintenance") : t(lang, "calOther")}</span>
              </button>
            ))}
          </div>

          <div className="cap-date-row">
            <div className="cap-field">
              <label className="cap-section-label">{t(lang, "calStartDate")}</label>
              <input type="date" className="cap-input" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="cap-arrow">→</div>
            <div className="cap-field">
              <label className="cap-section-label">{t(lang, "calEndDate")}</label>
              <input type="date" className="cap-input" value={dateTo}
                min={dateFrom}
                onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>

          {!colorLocked && (
            <>
              <div className="cap-section-label">{t(lang, "calColor")}</div>
              <div className="cap-palette">
                {PALETTE.map(c => (
                  <button key={c} className={`cap-color-btn ${color === c ? "selected" : ""}`}
                    style={{ background: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
                <label className="cap-color-custom" title={t(lang, "calChooseColor")}>
                  <input type="color" value={color} onChange={e => setColor(e.target.value)} />
                  <span style={{ background: color }} />
                </label>
              </div>
            </>
          )}

          <div className="cap-preview" style={{ background: color + "22", borderLeft: `4px solid ${color}` }}>
            <span style={{ color }}>{chosen.icon} {chosen.key === "Occupied" ? t(lang, "calOccupied") : chosen.key === "Maintenance" ? t(lang, "calMaintenance") : t(lang, "calOther")}</span>
            <span className="cap-preview-dates" style={{ color }}>{dateFrom} → {dateTo}</span>
          </div>

          {type === "Other" && (
            <>
              <div className="cap-section-label">{t(lang, "calReason")} <span style={{ color: "#ef4444" }}>*</span></div>
              <textarea className="cap-textarea" rows={3}
                placeholder={t(lang, "calReasonPh")}
                value={note} onChange={e => setNote(e.target.value)}
                style={{ borderColor: note.trim() ? "#e5e7eb" : "#fca5a5" }}
              />
              {!note.trim() && (
                <div style={{ fontSize: 11, color: "#ef4444", marginTop: -6 }}>{t(lang, "calReasonRequired")}</div>
              )}
            </>
          )}
          </>
          )}
        </div>

        <div className="bm-footer" style={{ flexDirection: "row", gap: 10 }}>
          {isEdit && (
            <button className="bm-btn-cancel"
              style={{ background: "#fef2f2", color: "#b91c1c", flex: "none", padding: "10px 14px" }}
              onClick={handleDeleteClick}>
              {t(lang, "calDelete")}
            </button>
          )}
          <button className="bm-btn-cancel" onClick={onClose}>{t(lang, "calCancel")}</button>
          <button className="bm-btn-close" disabled={saving}
            style={{ background: isBooking ? BOOK_COLOR : color }}
            onClick={isBooking ? handleBookingSave : handleSave}>
            {isBooking
              ? (saving ? t(lang, "calBkSubmitting") : t(lang, "calBkSubmit"))
              : (saving ? t(lang, "calSaving") : isEdit ? t(lang, "calSaveEdit") : t(lang, "calSave"))}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   CONFIRM MODAL
   ============================================================ */
function ConfirmModal({ info, onConfirm, onCancel, lang }) {
  if (!info) return null;
  const nights = Math.round(
    (new Date(info.newCheckOut) - new Date(info.newCheckIn)) / 86400000
  );
  return (
    <div className="bm-overlay" onClick={onCancel}>
      <div className="bm-card" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        {/* พื้นอ่อน + ตัวหนังสือเข้ม (ดู comment ใน BookingActionPopup) */}
        <div className="bm-header" style={{ background: "linear-gradient(135deg,#eef2ff,#e0e7ff)" }}>
          <div className="bm-header-left">
            <span className="bm-success-icon">📅</span>
            <div>
              <h3 className="bm-title" style={{ color: "#1e1b4b" }}>{info.newUnitId ? t(lang, "calChangeRoomDateQ") : t(lang, "calChangeDateQ")}</h3>
              <p className="bm-subtitle" style={{ color: "#4c5578" }}>
                {info.newUnitId
                  ? `ห้อง ${info.origRoomNumber} → ${info.roomNumber}`
                  : `ห้อง ${info.roomNumber}`}
              </p>
            </div>
          </div>
          <button className="bm-close" onClick={onCancel}>✕</button>
        </div>
        <div className="bm-body">
          <div className="bm-grid">
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calCheckNewIn")}</span>
              <span className="bm-value">{fmtDate(info.newCheckIn)}</span>
            </div>
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calCheckNewOut")}</span>
              <span className="bm-value">{fmtDate(info.newCheckOut)}</span>
            </div>
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calNights")}</span>
              <span className="bm-value">{nights} {t(lang, "calNightSuffix")}</span>
            </div>
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calStatus")}</span>
              <span className="bm-badge">{info.status}</span>
            </div>
            {info.origTotal > 0 && (
              <div className="bm-item" style={{ gridColumn: "1/-1" }}>
                <span className="bm-label">{t(lang, "calNewTotal")}</span>
                <span className="bm-value" style={{ color: info.newTotal !== info.origTotal ? "#b45309" : "#111827" }}>
                  {info.newTotal?.toLocaleString("th-TH")} THB
                  {info.newTotal !== info.origTotal && (
                    <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>
                      ({t(lang, "calOldTotal")} {info.origTotal?.toLocaleString("th-TH")} THB)
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="bm-footer" style={{ flexDirection: "row", gap: 10 }}>
          <button className="bm-btn-cancel" onClick={onCancel}>{t(lang, "calCancel")}</button>
          <button className="bm-btn-close" onClick={onConfirm}>{t(lang, "calConfirm")}</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   BOOKING ACTION POPUP — คลิกแถบการจองแล้วดูรายละเอียด / ยกเลิก
   ============================================================ */
const CANCELLABLE_STATUS = ["Pending", "Approved", "Confirmed", "CheckedIn"];

/* ขั้นตอนถัดไปของแต่ละสถานะ — ใช้ทั้งกำหนดปลายทางและข้อความบนปุ่ม
   staff กับ admin ใช้ route คนละชุด (staff ไม่มีสิทธิ์เข้า /admin/*)
   ถ้าส่ง staff ไป /admin/... ProtectedRoute จะเด้งกลับ /calendar ทันที */
const NEXT_STEP = {
  Pending:   { adminPath: "/admin/payments", staffPath: "/slips",    labelKey: "calNextSlip" },
  Approved:  { adminPath: "/admin/checkin",  staffPath: "/checkin",  labelKey: "calNextCheckIn" },
  Confirmed: { adminPath: "/admin/checkin",  staffPath: "/checkin",  labelKey: "calNextCheckIn" },
  CheckedIn: { adminPath: "/admin/checkout", staffPath: "/checkout", labelKey: "calNextCheckOut" },
};

function BookingActionPopup({ info, onClose, onCancelBooking, onNextStep, busy, statusLabel, lang }) {
  const [confirming, setConfirming] = useState(false);
  const [reason,     setReason]     = useState("");

  useEffect(() => { setConfirming(false); setReason(""); }, [info]);

  if (!info) return null;

  const { booking, unit } = info;
  const cfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG.Confirmed;

  const guestName = booking.user
    ? `${booking.user.firstname ?? ""} ${booking.user.lastname ?? ""}`.trim() || booking.user.email || booking.guestName || "—"
    : booking.guestName || "—";
  const phone  = booking.user?.phone || booking.guestInfo?.phone || "";
  const nights = Math.max(1, Math.round(
    (new Date(booking.checkOut) - new Date(booking.checkIn)) / 86400000
  ));
  const total = (booking.totalAmount || 0) + (booking.extraTotal || 0);
  const paid  = booking.paidAmount || 0;
  const due   = Math.max(0, total - paid);
  const canCancel = CANCELLABLE_STATUS.includes(booking.status);
  const nextStep  = NEXT_STEP[booking.status] || null;

  return (
    <div className="bm-overlay" onClick={() => !busy && onClose()}>
      <div className="bm-card" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>

        {/* หัวการ์ดใช้พื้นอ่อน + ตัวหนังสือเข้ม — ห้ามทับพื้นเป็นสีเข้ม เพราะ .bm-title
            ถูกกำหนดสีเขียวเข้มไว้ใน CSS จะกลายเป็นเขียวเข้มบนน้ำเงินเข้ม อ่านไม่ออก */}
        <div className="bm-header" style={{ background: "linear-gradient(135deg,#eef2ff,#e0e7ff)" }}>
          <div className="bm-header-left">
            <span className="bm-success-icon">🛏️</span>
            <div>
              <h3 className="bm-title" style={{ color: "#1e1b4b" }}>{guestName}</h3>
              <p className="bm-subtitle" style={{ color: "#4c5578" }}>
                {t(lang, "roomNoLabel")} {unit?.roomNumber ?? "—"}
                {booking.roomType?.name ? ` · ${booking.roomType.name}` : ""}
              </p>
            </div>
          </div>
          <button className="bm-close" onClick={() => !busy && onClose()}>✕</button>
        </div>

        <div className="bm-body">
          <div className="bm-grid">
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calStatus")}</span>
              <span className="bm-value" style={{ color: cfg.color }}>{statusLabel(booking.status)}</span>
            </div>
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calNights")}</span>
              <span className="bm-value">{nights} {t(lang, "calNightSuffix")}</span>
            </div>
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calCheckIn")}</span>
              <span className="bm-value">{fmtDate(booking.checkIn)}</span>
            </div>
            <div className="bm-item">
              <span className="bm-label">{t(lang, "calCheckOut")}</span>
              <span className="bm-value">{fmtDate(booking.checkOut)}</span>
            </div>
            {phone && (
              <div className="bm-item" style={{ gridColumn: "1/-1" }}>
                <span className="bm-label">{t(lang, "calBkPhone")}</span>
                <span className="bm-value">{phone}</span>
              </div>
            )}
            {total > 0 && (
              <>
                <div className="bm-item">
                  <span className="bm-label">{t(lang, "calBkTotal")}</span>
                  <span className="bm-value">฿{total.toLocaleString("th-TH")}</span>
                </div>
                <div className="bm-item">
                  <span className="bm-label">{t(lang, "paidAmountLbl")}</span>
                  <span className="bm-value" style={{ color: due > 0 ? "#b45309" : "#065f46" }}>
                    ฿{paid.toLocaleString("th-TH")}
                    {due > 0 && (
                      <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>
                        ({t(lang, "calDue")} ฿{due.toLocaleString("th-TH")})
                      </span>
                    )}
                  </span>
                </div>
              </>
            )}
          </div>

          {confirming && (
            <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10,
                          background: "#fef2f2", border: "1px solid #fecaca" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>
                ⚠️ {t(lang, "calCancelBkConfirm")}
              </div>
              <input
                className="cap-input"
                value={reason}
                placeholder={t(lang, "calCancelBkReasonPh")}
                onChange={e => setReason(e.target.value)}
              />
            </div>
          )}

          {!canCancel && (
            <div style={{ marginTop: 14, fontSize: 12, color: "#9ca3af" }}>
              🔒 {t(lang, "calCancelBkLocked")}
            </div>
          )}
        </div>

        <div className="bm-footer" style={{ flexDirection: "row", gap: 10 }}>
          {confirming ? (
            <>
              <button className="bm-btn-cancel" disabled={busy}
                onClick={() => setConfirming(false)}>
                {t(lang, "calCancelBkBack")}
              </button>
              <button className="bm-btn-close" disabled={busy}
                style={{ background: "#dc2626" }}
                onClick={() => onCancelBooking(booking, reason)}>
                {busy ? t(lang, "calCancelBkBusy") : t(lang, "calCancelBkYes")}
              </button>
            </>
          ) : (
            <>
              {/* ยกเลิก = งานรอง ทำให้เล็กและไม่เด่น / ขั้นตอนถัดไป = งานหลัก กินพื้นที่ที่เหลือ
                  ปิดหน้าต่างใช้ ✕ มุมขวาบนแทน ไม่ต้องมีปุ่มซ้ำ */}
              {canCancel && (
                <button className="bm-btn-cancel"
                  style={{ flex: "none", padding: "8px 14px", fontSize: 13,
                           background: "#fef2f2", color: "#b91c1c" }}
                  onClick={() => setConfirming(true)}>
                  {t(lang, "calCancelBkBtn")}
                </button>
              )}
              {nextStep && (
                <button className="bm-btn-close" onClick={() => onNextStep(booking)}>
                  {t(lang, nextStep.labelKey)}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   DAY HEADER TOOLTIP
   ============================================================ */
function DayTooltip({ day, month, year, units, bookings, blocks = [], style, lang }) {
  const targetDate = new Date(year, month - 1, day);

  let checkinCount = 0;
  const bookedUnitIds   = new Set();
  const occupiedUnitIds = new Set();

  bookings.forEach(b => {
    if (b.status === "CheckedOut") return;

    const ci     = new Date(b.checkIn);
    const co     = new Date(b.checkOut);
    const ciDate = new Date(ci.getFullYear(), ci.getMonth(), ci.getDate());
    const coDate = new Date(co.getFullYear(), co.getMonth(), co.getDate());
    const buId   = (b.roomUnit?._id ?? b.roomUnit)?.toString();

    if (b.status === "CheckedIn" && ciDate.getTime() === targetDate.getTime()) {
      checkinCount++;
    }

    if (
      ["Pending", "Approved", "Confirmed"].includes(b.status) &&
      ciDate <= targetDate && targetDate < coDate &&
      buId
    ) {
      bookedUnitIds.add(buId);
    }

    if (
      b.status === "CheckedIn" &&
      ciDate <= targetDate && targetDate < coDate &&
      buId
    ) {
      occupiedUnitIds.add(buId);
    }
  });

  /* ห้องที่ CheckedIn อยู่แล้ว ไม่นับซ้ำในฝั่งจอง */
  occupiedUnitIds.forEach(id => bookedUnitIds.delete(id));

  /* ห้องที่ถูก block (ซ่อม/ไม่ว่าง) ในวันนั้น นับเป็นไม่ว่างด้วย */
  const blockedUnitIds = new Set();
  blocks.forEach(bl => {
    const from = new Date(bl.dateFrom);
    const to   = new Date(bl.dateTo);
    const fromDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const toDate   = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    const uId = bl.unitId?.toString();
    if (uId && fromDate <= targetDate && targetDate <= toDate
        && !bookedUnitIds.has(uId) && !occupiedUnitIds.has(uId)) {
      blockedUnitIds.add(uId);
    }
  });

  const bookedCount    = bookedUnitIds.size;
  const occupiedCount  = occupiedUnitIds.size;
  const availableCount = Math.max(0, units.length - bookedCount - occupiedCount - blockedUnitIds.size);

  const rows = [
    { label: t(lang, "calCheckinCount"), value: checkinCount,   color: "#166534" },
    { label: t(lang, "calAvailCount"),   value: availableCount, color: "#374151" },
    { label: t(lang, "calBookedCount"),  value: bookedCount,    color: "#b45309" },
  ];

  return (
    <div style={{
      ...style,
      background: "#fff",
      borderRadius: 14,
      boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
      padding: "14px 20px",
      minWidth: 220,
      fontFamily: "'Noto Sans Thai', sans-serif",
      border: "1px solid #e5e7eb",
    }}>
      {rows.map((row, i) => (
        <div key={i} style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 0",
          borderBottom: i < rows.length - 1 ? "1px solid #f3f4f6" : "none",
        }}>
          <span style={{ fontSize: 13, color: "#374151" }}>{row.label}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: row.color, marginLeft: 16 }}>
            : {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
function AdminCalendar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const MONTHS_TH = t(lang, "calMonthsTH");
  const DOW_TH    = t(lang, "calDowTH");

  const [role,     setRole]     = useState(null);
  const [units,    setUnits]    = useState([]);
  const [bookings, setBookings] = useState([]);
  const [month,    setMonth]    = useState(new Date().getMonth() + 1);
  const [year,     setYear]     = useState(new Date().getFullYear());
  const [tooltip,  setTooltip]  = useState(null);
  const [dayTooltip, setDayTooltip] = useState(null);
  const [filterType,   setFilterType]   = useState("ทั้งหมด");
  const [filterStatus, setFilterStatus] = useState("ทั้งหมด");
  const [confirmInfo,  setConfirmInfo]  = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [cellPopup,    setCellPopup]    = useState(null);
  const [editBlock,    setEditBlock]    = useState(null);
  const [localBlocks,  setLocalBlocks]  = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toasting,     setToasting]     = useState(null);
  const [bookingPopup, setBookingPopup] = useState(null);   // การจองที่แอดมินคลิกเปิดดู
  const [cancelling,   setCancelling]   = useState(false);

  /* ---- โหมดเลือกหลายห้อง ----
     แต่ละห้องมีช่วงวันของตัวเอง (selRanges) เข้า-ออกคนละวันได้
     selIn/selOut = ช่วงตั้งต้น ใช้ตอนติ๊กห้องใหม่ และตอนกด "ตั้งวันให้ทุกห้อง" */
  const [selectMode, setSelectMode] = useState(false);
  const [selUnitIds, setSelUnitIds] = useState([]);          // เก็บเป็น array เพื่อคงลำดับที่ติ๊ก
  const [selRanges,  setSelRanges]  = useState({});          // { [unitId]: { in, out } }
  const [selIn,      setSelIn]      = useState(todayStr());
  const [selOut,     setSelOut]     = useState(addDaysStr(todayStr(), 1));

  const toast = (msg, type = "success") => {
    setToasting({ msg, type });
    setTimeout(() => setToasting(null), 2800);
  };

  /* ---- zoom ---- */
  const [zoomIdx, setZoomIdx] = useState(ZOOM_DEFAULT);
  const colWidth = ZOOM_LEVELS[zoomIdx];
  const zoomIn  = () => setZoomIdx(i => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  const zoomOut = () => setZoomIdx(i => Math.max(i - 1, 0));

  /* ---- day view (มุมมองรายวัน) ---- */
  const [selectedDay, setSelectedDay] = useState(null); // null = มุมมองทั้งเดือน
  const isDayView = selectedDay !== null;
  const openDayView  = (day) => { setSelectedDay(day); setDayTooltip(null); };
  const closeDayView = () => setSelectedDay(null);
  const stepDay = (delta) => {
    const d = new Date(year, month - 1, selectedDay + delta); // Date จัดการข้ามเดือน/ปีให้เอง
    setSelectedDay(d.getDate());
    setMonth(d.getMonth() + 1);
    setYear(d.getFullYear());
  };

  /* ---- drag state ---- */
  const dragRef = useRef({
    booking: null, startDay: null, startX: null, cellWidth: 36,
    origCheckIn: null, origCheckOut: null, unitId: null,
    mode: "move", // "move" | "resize-left" | "resize-right"
    origNights: 0, origTotal: 0,
  });
  /* แยก "ลากแถบ" ออกจาก "คลิกแถบ" — ขยับเกิน 4px ถือว่าลาก แล้วกลืน click ที่ตามมา */
  const movedRef         = useRef(false);
  const suppressClickRef = useRef(false);

  /* ลากเลือกช่วงวันบนกริด (โหมดเลือกหลายห้อง) — คนละระบบกับ dragRef ที่ใช้ย้าย/ยืดแถบการจองจริง
     anchorDay = ขอบที่อยู่กับที่, originDay = ขอบที่กำลังลาก */
  const selDragRef = useRef({ active: false, anchorDay: null, originDay: null, startX: 0, cellWidth: 36, lastDay: null });

  const [, setDragDay] = useState(null);
  const [dragId,      setDragId]      = useState(null);
  const [dragUnitId,  setDragUnitId]  = useState(null); // target unit during cross-unit drag
  const [dragNewIn,   setDragNewIn]   = useState(null);
  const [dragNewOut,  setDragNewOut]  = useState(null);
  const [dragOverlap, setDragOverlap] = useState(false);
  const [, setMousePos] = useState({ x: 0, y: 0 });

  const daysInMonth = new Date(year, month, 0).getDate();
  const todayD = new Date();
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, month - 1, i + 1);
    const isToday = d.getFullYear() === todayD.getFullYear() && d.getMonth() === todayD.getMonth() && d.getDate() === todayD.getDate();
    return { day: i + 1, dow: DOW_TH[d.getDay()], isWeekend: d.getDay() === 0 || d.getDay() === 6, isToday };
  });

  /* ---- load ---- */
  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const [uRes, bRes, blRes] = await Promise.all([
        fetch(`${API}/api/room-units`, { credentials: "include", headers: authHeaders }),
        fetch(`${API}/api/bookings?month=${month}&year=${year}`, { credentials: "include", headers: authHeaders }),
        fetch(`${API}/api/room-blocks?month=${month}&year=${year}`, { credentials: "include", headers: authHeaders }),
      ]);
      const uData  = await uRes.json();
      const bData  = await bRes.json();
      const blData = await blRes.json();
      const su  = Array.isArray(uData)  ? uData  : [];
      /* การจองที่ยกเลิกแล้วไม่ต้องขึ้นบนปฏิทิน — ห้องถือว่าว่างแล้ว */
      const sb  = Array.isArray(bData)  ? bData.filter(b => b.status !== "Cancelled") : [];
      const sbl = Array.isArray(blData) ? blData : [];
      su.sort((a, b) => a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true }));

      if (process.env.NODE_ENV === "development") {
        console.log("[AdminCalendar] units:", su.length, "bookings:", sb.length, "blocks:", sbl.length);
      }

      setUnits(su);
      setBookings(sb);
      setLocalBlocks(sbl);
    } catch (e) {
      console.error("[AdminCalendar] loadData error:", e);
      setUnits([]); setBookings([]);
    }
  }, [month, year]);

  useEffect(() => {
    const fetchRole = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRole(data.role);
        }
      } catch (e) {
        console.error("[AdminCalendar] fetchRole error:", e);
      }
    };
    fetchRole();
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* เปลี่ยนเดือนระหว่างอยู่ใน day view → clamp วันไม่ให้เกินจำนวนวันของเดือนใหม่ */
  useEffect(() => {
    setSelectedDay(sd => (sd == null ? sd : Math.min(sd, daysInMonth)));
  }, [daysInMonth]);

  useEffect(() => {
    const nb = location.state?.newBooking;
    if (nb?.checkIn) {
      const d = new Date(nb.checkIn);
      setMonth(d.getMonth() + 1);
      setYear(d.getFullYear());
    }
  }, [location.state]);

  /* ---- helpers ---- */
  const getBookingsForUnit = useCallback((unitId) =>
    bookings.filter(b => {
      const buId = (b.roomUnit?._id ?? b.roomUnit)?.toString();
      return buId === unitId?.toString();
    }),
  [bookings]);

  const getBarProps = useCallback((b) => {
    if (!b.checkIn || !b.checkOut) return null;
    if (b.status === "CheckedOut") return null;
    const ci = new Date(b.checkIn);
    const co = new Date(b.checkOut);
    const ciY = ci.getFullYear(), ciM = ci.getMonth() + 1, ciD = ci.getDate();
    const coY = co.getFullYear(), coM = co.getMonth() + 1, coD = co.getDate();
    const startDay = (ciY === year && ciM === month) ? ciD : 1;
    const endDay   = (coY === year && coM === month) ? coD - 1 : daysInMonth;
    if (startDay > daysInMonth || endDay < 1 || endDay < startDay) return null;
    return { startDay, endDay, span: endDay - startDay + 1 };
  }, [month, year, daysInMonth]);

  const statusLabel = (status) => {
    if (status === "CheckedIn") return t(lang, "calLabelCheckedIn");
    if (status === "Cancelled") return t(lang, "calLabelCancelled");
    if (status === "Maintenance") return t(lang, "calMaintenance");
    return t(lang, "calLabelBooked");
  };

  const roomTypes = ["ทั้งหมด", ...new Set(units.map(u => u.roomType?.name || t(lang, "calUnknownType")))];

  // สีประจำประเภท (ตามชื่อ) สำหรับจุดสีใน filter — legend ไปในตัว
  const typeColorByName = {};
  units.forEach(u => {
    const n = u.roomType?.name;
    if (n && !typeColorByName[n]) typeColorByName[n] = TYPE_COLORS[u.roomType?.type] || "#1a237e";
  });

  const filteredUnits = units.filter(u => {
    if (filterType !== "ทั้งหมด" && (u.roomType?.name || t(lang, "calUnknownType")) !== filterType) return false;

    if (filterStatus === "ห้องที่ถูกจองแล้ว") {
      const has = bookings.some(b => {
        const buId = (b.roomUnit?._id ?? b.roomUnit)?.toString();
        return buId === u._id?.toString() && ["Pending", "Approved", "Confirmed"].includes(b.status);
      });
      if (!has) return false;
    }

    if (filterStatus === "ห้องที่ลูกค้าเข้าพักอยู่") {
      const has = bookings.some(b => {
        const buId = (b.roomUnit?._id ?? b.roomUnit)?.toString();
        return buId === u._id?.toString() && b.status === "CheckedIn";
      });
      if (!has) return false;
    }

    if (filterStatus === "ห้องที่กำลังซ่อมแซม") {
      const hasBlock = localBlocks.some(b =>
        b.unitId?.toString() === u._id?.toString() &&
        (b.type === "Maintenance" || b.type === "Occupied")
      );
      const hasStatus = u.status === "Maintenance";
      if (!hasBlock && !hasStatus) return false;
    }

    return true;
  });

  // เรียงเลขห้องเป็นหลัก (ไม่จัดกลุ่มตามประเภท) — เลือกดูตามประเภทได้จาก filter ใน sidebar

  /* ============================================================
     DRAG HANDLERS
     ============================================================ */
  const handleBarMouseDown = (e, booking, unit, barProps, mode = "move") => {
    /* อยู่ในโหมดจองหลายห้อง — ห้ามย้าย/ยืดการจองเดิม กันเผลอลากทับตอนกำลังเลือกห้อง */
    if (selectMode) return;
    if (["CheckedOut", "Cancelled"].includes(booking.status)) return;
    e.preventDefault();
    setTooltip(null);
    const td = e.currentTarget.closest("td");
    const cw = td ? td.offsetWidth / barProps.span : 36;
    const origIn     = new Date(booking.checkIn);
    const origOut    = new Date(booking.checkOut);
    const origNights = Math.round((origOut - origIn) / 86400000);
    movedRef.current = false;
    dragRef.current  = {
      booking, startDay: barProps.startDay, startX: e.clientX, startY: e.clientY,
      cellWidth: cw, origCheckIn: booking.checkIn, origCheckOut: booking.checkOut,
      unitId: unit._id, mode, origNights, origTotal: booking.totalAmount || 0,
    };
    setDragId(booking._id);
    setDragUnitId(unit._id?.toString());
    setDragDay(barProps.startDay);
    setDragNewIn(origIn); setDragNewOut(origOut);
    setDragOverlap(false);
  };

  /* helper: compute newIn/newOut from drag state */
  const computeDragDates = (dr, dx) => {
    const delta   = Math.round(dx / dr.cellWidth);
    const origIn  = new Date(dr.origCheckIn);
    const origOut = new Date(dr.origCheckOut);
    let newIn = origIn, newOut = origOut;
    if (dr.mode === "move") {
      newIn  = new Date(origIn.getTime()  + delta * 86400000);
      newOut = new Date(origOut.getTime() + delta * 86400000);
    } else if (dr.mode === "resize-right") {
      newOut = new Date(origOut.getTime() + delta * 86400000);
      if (newOut <= origIn) newOut = new Date(origIn.getTime() + 86400000);
    } else if (dr.mode === "resize-left") {
      newIn = new Date(origIn.getTime() + delta * 86400000);
      if (newIn >= origOut) newIn = new Date(origOut.getTime() - 86400000);
    }
    return { newIn, newOut };
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      /* ── ลากเลือกช่วงวันบนกริด ── ต้องอยู่ก่อน guard ของ dragRef ด้านล่าง
         สองระบบเช็ค ref คนละตัว จึงไม่มีทางทำงานทับกัน */
      const sd = selDragRef.current;
      if (sd.active) {
        const sdx = e.clientX - sd.startX;
        if (Math.abs(sdx) > 4) movedRef.current = true;
        const delta  = Math.round(sdx / sd.cellWidth);
        const curDay = Math.max(1, Math.min(daysInMonth, sd.originDay + delta));
        if (curDay !== sd.lastDay) {          // อัปเดต state เฉพาะตอนข้ามวัน ไม่ใช่ทุก pixel
          sd.lastDay = curDay;
          const a = Math.min(sd.anchorDay, curDay);
          const b = Math.max(sd.anchorDay, curDay);
          const nIn  = ymd(year, month - 1, a);
          const nOut = addDaysStr(ymd(year, month - 1, b), 1);
          // เขียนเฉพาะห้องที่กำลังลาก ห้องอื่นไม่ขยับ
          setSelRanges(prev => ({ ...prev, [sd.unitId]: { in: nIn, out: nOut } }));
          setSelIn(nIn); setSelOut(nOut);     // จำเป็นช่วงตั้งต้นของห้องถัดไป
        }
        return;
      }

      const dr = dragRef.current;
      if (!dr.booking) return;
      const dx = e.clientX - dr.startX;
      if (Math.abs(dx) > 4 || Math.abs(e.clientY - (dr.startY ?? e.clientY)) > 4) {
        movedRef.current = true;
      }
      const { newIn, newOut } = computeDragDates(dr, dx);

      /* detect target unit from mouse Y (only for move mode) */
      let targetUnitId = dr.unitId?.toString();
      if (dr.mode === "move") {
        const rows = document.querySelectorAll("tr[data-unit-id]");
        for (const row of rows) {
          const rect = row.getBoundingClientRect();
          if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
            targetUnitId = row.getAttribute("data-unit-id");
            break;
          }
        }
      }
      setDragUnitId(targetUnitId);

      /* check overlap for live feedback using target unit */
      const overlapBooking = bookings.some(b => {
        if (b._id === dr.booking._id) return false;
        const buId = (b.roomUnit?._id ?? b.roomUnit)?.toString();
        if (buId !== targetUnitId) return false;
        if (["CheckedOut","Cancelled"].includes(b.status)) return false;
        return newIn < new Date(b.checkOut) && newOut > new Date(b.checkIn);
      });
      const overlapBlock = localBlocks.some(bl => {
        if (bl.unitId?.toString() !== targetUnitId) return false;
        return newIn < new Date(bl.dateTo) && newOut > new Date(bl.dateFrom);
      });
      const overlap = overlapBooking || overlapBlock;

      setDragNewIn(newIn); setDragNewOut(newOut);
      setDragOverlap(overlap);
      if (dr.mode === "move") {
        const delta = Math.round(dx / dr.cellWidth);
        setDragDay(Math.max(1, Math.min(daysInMonth, dr.startDay + delta)));
      }
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    const onMouseUp = (e) => {
      /* จบการลากเลือกช่วงวัน — กลืน click ที่ตามมา ไม่ให้ไปสลับติ๊กห้องซ้ำ */
      const sd = selDragRef.current;
      if (sd.active) {
        sd.active = false;
        suppressClickRef.current = true;
        movedRef.current = false;
        setTimeout(() => { suppressClickRef.current = false; }, 0);
        return;
      }

      const dr = dragRef.current;
      if (!dr.booking) return;
      const dx = e.clientX - dr.startX;
      const { newIn, newOut } = computeDragDates(dr, dx);
      const delta = Math.round(dx / dr.cellWidth);

      // get current target unit from DOM (same logic as mousemove)
      let targetUnitId = dr.unitId?.toString();
      if (dr.mode === "move") {
        const rows = document.querySelectorAll("tr[data-unit-id]");
        for (const row of rows) {
          const rect = row.getBoundingClientRect();
          if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
            targetUnitId = row.getAttribute("data-unit-id");
            break;
          }
        }
      }

      const unitChanged = targetUnitId !== dr.unitId?.toString();

      if (delta !== 0 || unitChanged) {
        /* block overlap against target unit */
        const overlapsBooking = bookings.some(b => {
          if (b._id === dr.booking._id) return false;
          const buId = (b.roomUnit?._id ?? b.roomUnit)?.toString();
          if (buId !== targetUnitId) return false;
          if (["CheckedOut","Cancelled"].includes(b.status)) return false;
          return newIn < new Date(b.checkOut) && newOut > new Date(b.checkIn);
        });
        const overlapsBlock = localBlocks.some(bl => {
          if (bl.unitId?.toString() !== targetUnitId) return false;
          return newIn < new Date(bl.dateTo) && newOut > new Date(bl.dateFrom);
        });
        const overlaps = overlapsBooking || overlapsBlock;

        if (!overlaps) {
          const newNights  = Math.round((newOut - newIn) / 86400000);
          const targetUnit = units.find(u => u._id?.toString() === targetUnitId);
          const origUnit   = units.find(u => u._id === dr.unitId);

          // Use target room type's price if room type changed, else keep per-night ratio
          const targetTypePrice = targetUnit?.roomType?.price;
          const origTypePrice   = origUnit?.roomType?.price;
          const typeChanged     = unitChanged && targetTypePrice && origTypePrice && targetTypePrice !== origTypePrice;
          const pricePerNight   = typeChanged
            ? targetTypePrice
            : (dr.origNights > 0 ? dr.origTotal / dr.origNights : (targetTypePrice || 0));
          const newTotal = Math.round(pricePerNight * newNights);
          setConfirmInfo({
            bookingId: dr.booking._id,
            roomNumber:     targetUnit?.roomNumber || "-",
            origRoomNumber: origUnit?.roomNumber   || "-",
            newUnitId:   unitChanged ? targetUnitId : null,
            status: dr.booking.status,
            newCheckIn:  newIn.toISOString(),  newCheckOut: newOut.toISOString(),
            origCheckIn: dr.origCheckIn,       origCheckOut: dr.origCheckOut,
            newTotal, origTotal: dr.origTotal, newNights,
          });
        }
      }

      /* กดแล้วปล่อยโดยไม่ขยับ = "คลิก" → เปิดป็อปอัปรายละเอียด/ยกเลิก
         ต้องดักที่ mouseup ไม่ใช่ event click เพราะพอ mousedown ทำให้ setDragId ทำงาน
         แถบเดิมจะถูก unmount แล้วแทนด้วย ghost <td> — DOM node ที่ผู้ใช้กดหายไปก่อน
         เบราว์เซอร์จะยิง click ทำให้ onClick บน <td> ไม่มีวันทำงาน */
      const wasClick = !movedRef.current && delta === 0 && !unitChanged;
      if (wasClick) {
        const clickedUnit = units.find(u => u._id?.toString() === dr.unitId?.toString());
        setTooltip(null);
        setBookingPopup({ booking: dr.booking, unit: clickedUnit });
      }

      /* กลืน click ที่เบราว์เซอร์อาจยิงตามมา ไม่ให้เปิดป็อปอัปซ้ำหรือเด้งหลังลากเสร็จ */
      suppressClickRef.current = true;
      movedRef.current = false;
      setTimeout(() => { suppressClickRef.current = false; }, 0);

      dragRef.current = { ...dragRef.current, booking: null };
      setDragId(null); setDragDay(null); setDragUnitId(null);
      setDragNewIn(null); setDragNewOut(null);
      setDragOverlap(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
    };
  // year/month ต้องอยู่ใน deps ด้วย — สาขาลากเลือกวันใช้ค่านี้สร้างวันที่
  // (daysInMonth ไม่พอ เพราะเปลี่ยนเดือนจาก ม.ค. ไป มี.ค. ค่ามันเท่ากัน closure จะค้างเดือนเก่า)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, daysInMonth, bookings, localBlocks, year, month]);

  /* ---- save after confirm ---- */
  const handleConfirmSave = async () => {
    if (!confirmInfo || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/bookings/${confirmInfo.bookingId}`, {
        method: "PUT",
        headers: getAuthHeaders(true),
        credentials: "include",
        body: JSON.stringify({
          checkIn:     confirmInfo.newCheckIn,
          checkOut:    confirmInfo.newCheckOut,
          totalAmount: confirmInfo.newTotal,
          ...(confirmInfo.newUnitId ? { roomUnit: confirmInfo.newUnitId } : {}),
        }),
      });
      if (res.ok) {
        setConfirmInfo(null);
        await loadData();
        toast(t(lang, "calToastSaved"));
      } else {
        toast(t(lang, "calToastSaveFail"), "error");
      }
    } catch {
      toast(t(lang, "calToastError"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCancel = () => {
    setConfirmInfo(null);
    setDragId(null);
    setDragDay(null);
  };

  /* ---- คลิกแถบการจอง → เปิดป็อปอัปรายละเอียด/ยกเลิก ---- */
  const handleBookingClick = (e, booking, unit) => {
    /* โหมดจองหลายห้อง — ไม่เปิดป็อปอัปจัดการการจองเดิม */
    if (selectMode) { e.stopPropagation(); return; }
    /* ต้องอ่านจาก ref เท่านั้น — state dragId ยังไม่ถูก flush ตอน click ยิงต่อจาก mouseup
       ถ้าเช็ค dragId จะโดนกลืนทุกครั้ง ป็อปอัปไม่เด้ง */
    if (suppressClickRef.current || dragRef.current.booking) return;
    e.stopPropagation();
    setTooltip(null);
    setBookingPopup({ booking, unit });
  };

  /* ส่งต่อไปหน้าที่ทำขั้นตอนถัดไปจริง พร้อม bookingId ให้หน้านั้นเลือกรายการให้เลย */
  const handleNextStep = (booking) => {
    const step = NEXT_STEP[booking.status];
    if (!step) return;
    /* role มาจาก /api/profile แบบ async — ถ้ายังไม่มาให้ใช้ค่าใน localStorage แทน
       ไม่งั้นคลิกเร็ว ๆ ตอนเพิ่งเปิดหน้าจะถูกมองเป็น staff ทั้งที่เป็น admin */
    const effRole = role || localStorage.getItem("role");
    navigate(effRole === "admin" ? step.adminPath : step.staffPath, {
      state: { bookingId: booking._id },
    });
  };

  const handleCancelBooking = async (booking, reason) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API}/api/admin/bookings/${booking._id}/cancel`, {
        method: "POST",
        headers: getAuthHeaders(true),
        credentials: "include",
        body: JSON.stringify({ reason: reason?.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast(data.message || t(lang, "calToastSaveFail"), "error");
        return;
      }
      setBookingPopup(null);
      await loadData();
      toast(t(lang, "calCancelBkOk"));
    } catch {
      toast(t(lang, "calToastConnErr"), "error");
    } finally {
      setCancelling(false);
    }
  };

  /* ---- cell click ---- */
  const handleCellClick = (e, unit, day) => {
    if (dragId || suppressClickRef.current) return;
    e.stopPropagation();
    /* โหมดเลือกหลายห้อง: การเลือกเกิดที่ onMouseDown ไปแล้ว (ตั้งวัน + ติ๊กห้อง)
       ตรงนี้จึงไม่ต้องทำอะไร ไม่งั้นจะสลับติ๊กกลับทันทีที่ปล่อยเมาส์ */
    if (selectMode) return;
    const dateStr = `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    setCellPopup({ unitId: unit._id, roomNumber: unit.roomNumber, date: dateStr, unit });
  };

  /* ห้องนี้ว่างตลอดช่วง [from, to) หรือไม่ — เช็คทั้งการจองและบล็อก
     (backend เช็คแค่การจอง ไม่เช็คบล็อก จึงต้องกันฝั่งนี้ด้วย) */
  const isUnitFree = (unitId, from, to) => {
    const start = new Date(`${from}T00:00:00Z`);
    const end   = new Date(`${to}T00:00:00Z`);
    const uid   = unitId?.toString();
    const hitsBooking = bookings.some(b => {
      const buId = (b.roomUnit?._id ?? b.roomUnit)?.toString();
      if (buId !== uid) return false;
      if (b.isBlock) return false;
      if (["CheckedOut", "Cancelled"].includes(b.status)) return false;
      return start < new Date(b.checkOut) && end > new Date(b.checkIn);
    });
    const hitsBlock = localBlocks.some(bl => {
      if (bl.unitId?.toString() !== uid) return false;
      // dateTo ของบล็อกเป็นวันสุดท้ายแบบ "รวมวันนั้น" (ดูการ render ที่ renderCells)
      // จึงต้อง +1 วันก่อนเทียบแบบช่วงเปิด ไม่งั้นจะจองทับวันสุดท้ายของบล็อกได้
      const blockEnd = new Date(bl.dateTo);
      blockEnd.setDate(blockEnd.getDate() + 1);
      return start < blockEnd && end > new Date(bl.dateFrom);
    });
    return !hitsBooking && !hitsBlock;
  };

  /* ============================================================
     โหมดเลือกหลายห้อง
     ============================================================ */
  const selNights  = (selIn && selOut) ? nightsBetween(selIn, selOut) : 0;
  const selRangeOk = selNights >= 1;

  /* ช่วงวันของห้องนั้น — ยังไม่ติ๊กก็ใช้ช่วงตั้งต้นไปก่อน (ใช้ตัดสินว่า checkbox กดได้ไหม) */
  const rangeOf = useCallback((unitId) =>
    selRanges[unitId?.toString()] || { in: selIn, out: selOut },
  [selRanges, selIn, selOut]);

  /* ห้องนี้ติ๊กได้ไหม — เช็คด้วยช่วงวันของห้องนั้นเอง */
  const canPickUnit = useCallback((unitId) => {
    const r = rangeOf(unitId);
    if (!r.in || !r.out || nightsBetween(r.in, r.out) < 1) return false;
    return isUnitFree(unitId, r.in, r.out);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rangeOf, bookings, localBlocks]);

  const selectedUnits = selUnitIds
    .map(id => units.find(u => u._id?.toString() === id))
    .filter(Boolean);

  const selTotal = selectedUnits.reduce((sum, u) => {
    const r = rangeOf(u._id);
    return sum + (Number(u.roomType?.price) || 0) * Math.max(0, nightsBetween(r.in, r.out));
  }, 0);

  /* ทุกห้องใช้ช่วงวันเดียวกันหรือไม่ — ใช้ตัดสินว่าจะโชว์ "หลายช่วง" ในแถบ */
  const allSameRange = selectedUnits.every(u => {
    const r = rangeOf(u._id);
    const f = rangeOf(selectedUnits[0]._id);
    return r.in === f.in && r.out === f.out;
  });

  const setUnitRange = (unitId, nextIn, nextOut) =>
    setSelRanges(prev => ({ ...prev, [unitId?.toString()]: { in: nextIn, out: nextOut } }));

  const toggleUnit = (unitId) => {
    const id = unitId?.toString();
    setSelUnitIds(prev => {
      if (prev.includes(id)) {
        setSelRanges(r => { const n = { ...r }; delete n[id]; return n; });
        return prev.filter(x => x !== id);
      }
      // ติ๊กใหม่ = ใช้ช่วงตั้งต้น (ช่วงล่าสุดที่ลาก) เคสปกติที่ทุกห้องวันเดียวกันจึงยังลากครั้งเดียวพอ
      setSelRanges(r => ({ ...r, [id]: { in: selIn, out: selOut } }));
      return [...prev, id];
    });
  };

  const clearSelection = () => { setSelUnitIds([]); setSelRanges({}); };

  /* แปลงช่วงวันของห้องหนึ่ง เป็น "วันที่เท่าไหร่ถึงเท่าไหร่" ของเดือนที่กำลังดูอยู่
     out เป็นวันเช็คเอาท์ (ไม่นับเป็นคืน) คืนสุดท้ายจึงเป็น out - 1 วัน */
  const draftRangeOf = useCallback((unitId) => {
    if (!selectMode) return null;
    const r = rangeOf(unitId);
    if (!r.in || !r.out || nightsBetween(r.in, r.out) < 1) return null;
    const mStart = new Date(Date.UTC(year, month - 1, 1));
    const mEnd   = new Date(Date.UTC(year, month - 1, daysInMonth));
    const s      = new Date(`${r.in}T00:00:00Z`);
    const last   = new Date(new Date(`${r.out}T00:00:00Z`).getTime() - 86400000);
    if (last < mStart || s > mEnd) return null;                 // ช่วงของห้องนี้ไม่อยู่ในเดือนนี้เลย
    return {
      startDay: s    < mStart ? 1           : s.getUTCDate(),
      endDay:   last > mEnd   ? daysInMonth : last.getUTCDate(),
    };
  }, [selectMode, rangeOf, year, month, daysInMonth]);

  /* กดที่กล่องว่าง = ตั้งช่วงวัน "ของห้องนั้น" + ติ๊กห้องนั้น แล้วลากต่อเพื่อขยายได้ */
  const handleSelMouseDown = (e, unit, day) => {
    if (!selectMode || dragId) return;
    e.preventDefault();
    const id = unit._id?.toString();
    const cw = e.currentTarget.offsetWidth || colWidth;
    selDragRef.current = {
      active: true, unitId: id, anchorDay: day, originDay: day,
      startX: e.clientX, cellWidth: cw, lastDay: day,
    };
    movedRef.current = false;
    const nextIn  = ymd(year, month - 1, day);
    const nextOut = addDaysStr(nextIn, 1);
    setUnitRange(id, nextIn, nextOut);
    setSelIn(nextIn); setSelOut(nextOut);        // จำไว้เป็นช่วงตั้งต้นของห้องถัดไป
    setSelUnitIds(prev => prev.includes(id) ? prev : [...prev, id]);
  };

  /* จับขอบร่างของห้องนั้นแล้วลากยืด/หด — ขอบตรงข้ามอยู่กับที่ ห้องอื่นไม่ขยับ
     originDay = วันที่ "ที่จับ" วางอยู่จริง (อาจไม่ตรงขอบช่วง ถ้าขอบไปตกในวันที่มีการจอง)
     ต้องใช้วันของที่จับ ไม่งั้นตำแหน่งเมาส์กับร่างจะเหลื่อมกันตอนลาก */
  const handleDraftResize = (e, unit, edge, originDay) => {
    if (dragId) return;
    const id = unit._id?.toString();
    const dr = draftRangeOf(unit._id);
    if (!dr) return;
    e.preventDefault();
    e.stopPropagation();
    const cw = e.currentTarget.closest("td")?.offsetWidth || colWidth;
    const origin = originDay ?? (edge === "start" ? dr.startDay : dr.endDay);
    selDragRef.current = {
      active: true, unitId: id,
      anchorDay: edge === "start" ? dr.endDay : dr.startDay,
      originDay: origin,
      startX: e.clientX, cellWidth: cw,
      lastDay: origin,
    };
    movedRef.current = false;
  };

  const exitSelectMode = () => { setSelectMode(false); setSelUnitIds([]); setSelRanges({}); };

  /* ห้องที่เลือกไว้แล้วช่วงวันไปทับการจอง/บล็อกอื่น
     ไม่ปลดติ๊กอัตโนมัติ — ปล่อยให้เห็นเป็นสีแดงบนปฏิทิน แล้วบล็อกปุ่มยืนยันไว้
     (ถ้าปลดเงียบๆ แอดมินจะงงว่าห้องหายไปไหน) */
  const conflictUnits = selectedUnits.filter(u => !canPickUnit(u._id));
  const hasConflict   = conflictUnits.length > 0;

  /* เลือกห้องว่างทั้งหมดที่มองเห็นอยู่ (เคารพ filter ใน sidebar) — ใช้ช่วงตั้งต้นให้ทุกห้อง */
  const selectAllFree = () => {
    const ok = filteredUnits.filter(u => selRangeOk && isUnitFree(u._id, selIn, selOut));
    setSelUnitIds(ok.map(u => u._id?.toString()));
    setSelRanges(Object.fromEntries(ok.map(u => [u._id?.toString(), { in: selIn, out: selOut }])));
  };

  /* ตั้งวันให้ทุกห้องที่ติ๊กไว้พร้อมกัน (ทับช่วงเฉพาะห้องที่ตั้งไว้) */
  const applyRangeToAll = (nextIn, nextOut) => {
    setSelIn(nextIn); setSelOut(nextOut);
    setSelRanges(prev => {
      const n = { ...prev };
      for (const id of selUnitIds) n[id] = { in: nextIn, out: nextOut };
      return n;
    });
  };

  const openGroupBookingPopup = () => {
    if (!selectedUnits.length) return;
    if (selectedUnits.some(u => !canPickUnit(u._id))) return;
    setCellPopup({
      units: selectedUnits,
      ranges: Object.fromEntries(selectedUnits.map(u => [u._id?.toString(), rangeOf(u._id)])),
      unitId: selectedUnits[0]._id,
      unit: selectedUnits[0],
      roomNumber: selectedUnits.map(u => u.roomNumber).join(", "),
      date: rangeOf(selectedUnits[0]._id).in,
      checkIn: rangeOf(selectedUnits[0]._id).in,
      checkOut: rangeOf(selectedUnits[0]._id).out,
    });
  };

  const handleCreateBooking = async ({ units: bookUnits, rooms, checkIn, checkOut, guestName, phone, paidAmount, checkInNow }) => {
    /* ติ๊ก "เช็คอินเลย" → ยังไม่บันทึกอะไรลงระบบตอนนี้
       ส่งรายละเอียดการจองไปเป็น draft ให้หน้าเช็คอินกรอกข้อมูลผู้เข้าพักก่อน
       แล้วค่อยสร้างใบจอง + เช็คอินพร้อมกันในครั้งเดียวตอนกดยืนยัน (POST /api/admin/checkin-booking)
       ถ้าเจ้าหน้าที่กรอกไม่จบหรือเช็คอินไม่สำเร็จ จะไม่มีใบจองค้างกินห้องไว้ในปฏิทิน */
    if (checkInNow) {
      const roomList = (rooms && rooms.length) ? rooms : bookUnits.map(u => ({ unitId: u._id }));
      const draft = {
        rooms: roomList.map(r => {
          const u = bookUnits.find(x => String(x._id) === String(r.unitId));
          return {
            unitId:     String(r.unitId),
            roomNumber: u?.roomNumber ?? "",
            roomTypeId: String(u?.roomType?._id ?? u?.roomType ?? ""),
            price:      Number(u?.roomType?.price) || 0,
            checkIn:    r.checkIn  || checkIn,
            checkOut:   r.checkOut || checkOut,
            guestName:  (r.guestName || "").trim() || guestName || "",
            phone:      (r.phone     || "").trim() || phone     || "",
          };
        }),
        guestName, phone, paidAmount,
      };
      exitSelectMode();
      const effRole = role || localStorage.getItem("role");
      navigate(effRole === "admin" ? "/admin/checkin" : "/checkin", { state: { draft } });
      return { ok: true };
    }

    try {
      const res = await fetch(`${API}/api/admin/group-booking`, {
        method: "POST",
        headers: getAuthHeaders(true),
        credentials: "include",
        // ไม่มี checkInNow ตรงนี้ — เส้นทาง "เช็คอินเลย" ถูกดักไปทำ draft ไปแล้วด้านบน
        body: JSON.stringify({
          rooms: rooms || bookUnits.map(u => ({ unitId: u._id })),
          checkIn, checkOut, guestName, phone, paidAmount,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, message: data.message };
      await loadData();
      exitSelectMode();
      toast(bookUnits.length > 1
        ? t(lang, "calBkToastGroupOk", bookUnits.length, data.roomNumber ?? "")
        : t(lang, "calBkToastOk").replace("{room}", data.roomNumber ?? bookUnits[0]?.roomNumber ?? ""));
      return { ok: true };
    } catch {
      return { ok: false, message: t(lang, "calToastConnErr") };
    }
  };

  const handleCellSave = async ({ _id, unitId, type, color, note, dateFrom, dateTo }) => {
    try {
      const method = _id ? "PUT" : "POST";
      const url    = _id
        ? `${API}/api/room-blocks/${_id}`
        : `${API}/api/room-blocks`;
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(true),
        credentials: "include",
        body: JSON.stringify({ unitId, type, color, note, dateFrom, dateTo }),
      });
      if (res.ok) {
        await loadData();
        toast(_id ? t(lang, "calToastBlockEdited") : t(lang, "calToastBlockSaved"));
        return;
      }
      toast(t(lang, "calToastSaveFail"), "error");
    } catch {
      toast(t(lang, "calToastConnErr"), "error");
    }
  };

  const handleDelete = async (target) => {
    const del = target || deleteTarget;
    if (!del) return;
    try {
      const res = await fetch(`${API}/api/room-blocks/${del._id}`, {
        method: "DELETE", credentials: "include", headers: getAuthHeaders(),
      });
      if (res.ok) {
        setLocalBlocks(prev => prev.filter(b => b._id !== del._id));
        toast(t(lang, "calToastDeleted"));
      } else {
        toast(t(lang, "calToastDeleteFail"), "error");
      }
    } catch {
      toast(t(lang, "calToastConnErr"), "error");
    }
    setDeleteTarget(null);
  };

  /* ============================================================
     TOOLTIP POSITION HELPER
     ============================================================ */
  const getTooltipStyle = (clientX, clientY) => {
    const TW = 230;
    const TH = 110;
    const OFFSET = 14;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = clientX + TW + OFFSET > vw ? clientX - TW - OFFSET : clientX + OFFSET;
    const top  = clientY + TH + OFFSET > vh ? clientY - TH - OFFSET : clientY + OFFSET;
    return { position: "fixed", top: Math.max(8, top), left: Math.max(8, left), zIndex: 9999 };
  };

  const getDayTooltipStyle = (clientX, clientY) => {
    const TW = 260;
    const OFFSET = 8;
    const vw = window.innerWidth;
    const left = clientX + TW + OFFSET > vw ? clientX - TW - OFFSET : clientX + OFFSET;
    return { position: "fixed", top: clientY + OFFSET, left: Math.max(8, left), zIndex: 9999 };
  };

  /* ============================================================
     RENDER CELLS
     ============================================================ */
  const renderCells = (unit) => {
    const unitBookings = getBookingsForUnit(unit._id);
    const unitBlocks   = localBlocks.filter(b => b.unitId?.toString() === unit._id?.toString());

    /* ที่จับขอบร่างวางได้เฉพาะบนกล่องว่าง (วันที่มีการจองถูกยุบเป็น colSpan ไปแล้ว)
       ถ้าร่างไปทับการจอง ขอบจริงอาจตกอยู่ในวันที่ไม่มีกล่องว่างให้เกาะ
       จึงเลื่อนที่จับมาไว้ที่วันว่าง "แรกสุด/ท้ายสุด" ภายในช่วงแทน — ไม่งั้นจะปรับช่วงต่อไม่ได้เลย */
    const rowDraftRange = (selectMode && selUnitIds.includes(unit._id?.toString()))
      ? draftRangeOf(unit._id) : null;
    let handleFirst = null, handleLast = null;
    if (rowDraftRange) {
      for (let d = rowDraftRange.startDay; d <= rowDraftRange.endDay; d++) {
        const ds = ymd(year, month - 1, d);
        if (isUnitFree(unit._id, ds, addDaysStr(ds, 1))) {
          if (handleFirst === null) handleFirst = d;
          handleLast = d;
        }
      }
    }

    // ---- drag ghost pre-calculation ----
    const unitIdStr        = unit._id?.toString();
    const origDragUnitStr  = dragId ? dragRef.current.unitId?.toString() : null;
    const targetDragUnitStr= dragId ? (dragUnitId?.toString() ?? origDragUnitStr) : null;

    const isDraggingOrigUnit = dragId && origDragUnitStr === unitIdStr;
    const isGhostUnit        = dragId && targetDragUnitStr === unitIdStr;

    // booking data from dragRef (available in any unit row)
    const draggingBkData = dragId ? dragRef.current.booking : null;
    const origBar        = draggingBkData ? getBarProps(draggingBkData) : null;
    const dragMode       = dragRef.current.mode;

    let ghostStartDay = null, ghostSpan = null;
    if (draggingBkData && origBar && dragNewIn && dragNewOut) {
      const din  = new Date(dragNewIn);
      const dout = new Date(dragNewOut);
      const mStart = new Date(year, month - 1, 1);
      const mEnd   = new Date(year, month, 0);
      if (din <= mEnd) {
        ghostStartDay = (din.getFullYear() === year && din.getMonth() + 1 === month) ? din.getDate() : 1;
      }
      if (ghostStartDay !== null && dout > mStart) {
        const endD = (dout.getFullYear() === year && dout.getMonth() + 1 === month)
          ? dout.getDate() - 1 : daysInMonth;
        ghostSpan = Math.max(1, Math.min(endD, daysInMonth) - ghostStartDay + 1);
      }
    }

    // helpers
    const buildBarContent = (bk, barCfg, isDraggable, isGhost, origBarForHandlers, barSpan = 1) => {
      const cfg = STATUS_CONFIG[bk.status] || STATUS_CONFIG.Confirmed;
      const guestName = bk.user
        ? `${bk.user.firstname ?? ""} ${bk.user.lastname ?? ""}`.trim() || bk.user.email || bk.guestName || "—"
        : bk.guestName || "—";
      const overlap = isGhost && dragOverlap;

      /* แสดงข้อความตามความกว้างจริงของแถบ — แคบเกินไม่ยัดข้อความ */
      const barWidth   = barSpan * colWidth;
      const showStatus = barWidth >= 150;   // กว้างพอ: สถานะ + ชื่อแขก
      const showGuest  = true;              // ชื่อแขกแสดงเสมอ (ตัดด้วย … ถ้าแคบ)

      return (
        <div className="cal-bar"
          title={`${guestName} · ${statusLabel(bk.status)}`}
          style={{ borderLeft: `4px solid ${overlap ? "#ef4444" : cfg.border}`,
                   position: "relative", cursor: isDraggable ? (isGhost ? "grabbing" : "grab") : "default",
                   opacity: isGhost ? 0.82 : 1,
                   paddingLeft: showGuest ? 8 : 2, gap: 6 }}
          onMouseDown={(!isGhost && isDraggable) ? e => handleBarMouseDown(e, bk, unit, origBarForHandlers, "move") : undefined}
        >
          {isDraggable && !isGhost && (
            <div className="cal-resize-handle cal-resize-left"
              onMouseDown={e => { e.stopPropagation(); handleBarMouseDown(e, bk, unit, origBarForHandlers, "resize-left"); }} />
          )}
          {overlap && <span style={{ fontSize: 12, flexShrink: 0 }}>⚠️</span>}
          {!overlap && showStatus && (
            <span className="cal-bar-label" style={{ color: cfg.color, flexShrink: 0 }}>
              {statusLabel(bk.status)}
              {!isDraggable && <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>🔒</span>}
            </span>
          )}
          {showGuest && (
            <span className="cal-bar-guest"
              style={{ color: cfg.color, fontSize: 11, fontWeight: 600, opacity: 0.85,
                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                       minWidth: 0, flex: 1 }}>
              {guestName}
            </span>
          )}
          {isDraggable && !isGhost && (
            <div className="cal-resize-handle cal-resize-right"
              onMouseDown={e => { e.stopPropagation(); handleBarMouseDown(e, bk, unit, origBarForHandlers, "resize-right"); }} />
          )}
        </div>
      );
    };

    const cells = [];
    let d = 1;

    // Build a "virtual timeline" for this unit during drag
    // Key: we must keep total colSpan = daysInMonth so other rows don't shift
    // Strategy: the dragging booking's original span is REMOVED from the row,
    // and a ghost is INSERTED at the new position — net change = 0 (for move).

    // origOccupied: days to free in the ORIGINAL unit row
    const origOccupied = isDraggingOrigUnit && origBar && dragMode !== "resize-right"
      ? { start: origBar.startDay, end: origBar.startDay + origBar.span - 1 }
      : null;
    // ghostOccupied: days the ghost bar occupies in the TARGET unit row
    const ghostOccupied = isGhostUnit && origBar && ghostStartDay !== null && dragMode !== "resize-right"
      ? { start: ghostStartDay, end: ghostStartDay + (ghostSpan ?? 0) - 1 }
      : null;

    while (d <= daysInMonth) {
      const currentDay = d;

      // Render ghost at its new position (target unit row)
      if (ghostOccupied && currentDay === ghostOccupied.start) {
        const cfg  = STATUS_CONFIG[draggingBkData.status] || STATUS_CONFIG.Confirmed;
        const span = Math.max(1, Math.min(ghostSpan ?? 1, daysInMonth - currentDay + 1));
        cells.push(
          <td key={`ghost-${dragId}`} colSpan={span} className="cal-td-booking"
            style={{ background: dragOverlap ? "#fef2f2" : cfg.bg,
                     outline: dragOverlap ? "2px solid #ef4444" : "none" }}>
            {buildBarContent(draggingBkData, null, false, true, origBar, span)}
          </td>
        );
        d += span;
        continue;
      }

      // Original booking span: render empty cell to keep column count stable
      if (origOccupied && currentDay >= origOccupied.start && currentDay <= origOccupied.end) {
        cells.push(
          <td key={`empty-${currentDay}`}
            className={`cal-td ${days[currentDay-1]?.isWeekend ? "weekend-cell" : ""} ${days[currentDay-1]?.isToday ? "today-cell" : ""}`}
          />
        );
        d++;
        continue;
      }

      const booking = unitBookings.find(b => {
        const bar = getBarProps(b);
        return bar && bar.startDay === currentDay;
      });

      const block = unitBlocks.find(b => {
        const from       = new Date(b.dateFrom);
        const to         = new Date(b.dateTo);
        const monthStart = new Date(year, month - 1, 1);
        if (to < monthStart) return null;                       // จบก่อนเดือนนี้
        const fromDay = (from.getFullYear() === year && from.getMonth() + 1 === month)
          ? from.getDate()
          : (from < monthStart ? 1 : null);                     // เริ่มก่อนเดือนนี้ → แสดงตั้งแต่วันที่ 1
        return fromDay === currentDay;
      });

      if (booking) {
        const bar = getBarProps(booking);
        const cfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG.Confirmed;
        const isDragging  = dragId === booking._id;
        const isDraggable = !["CheckedOut", "Cancelled"].includes(booking.status);

        if (isDragging && dragMode === "resize-right" && ghostSpan !== null) {
          // resize-right: render in-place with new span
          const span = Math.max(1, Math.min(ghostSpan, daysInMonth - currentDay + 1));
          cells.push(
            <td key={`b-${currentDay}`} colSpan={span} className="cal-td-booking"
              style={{ background: dragOverlap ? "#fef2f2" : cfg.bg,
                       outline: dragOverlap ? "2px solid #ef4444" : "none" }}>
              {buildBarContent(booking, bar, isDraggable, true, bar, span)}
            </td>
          );
          d += span;

        } else {
          // normal render
          const spanCapped = Math.min(bar.span, daysInMonth - bar.startDay + 1);
          cells.push(
            <td key={`b-${currentDay}`} colSpan={Math.max(1, spanCapped)}
              className="cal-td-booking" style={{ background: cfg.bg }}
              onClick={e => handleBookingClick(e, booking, unit)}
              onMouseEnter={e => setTooltip({ x: e.clientX, y: e.clientY, booking })}
              onMouseLeave={() => setTooltip(null)}>
              {buildBarContent(booking, bar, isDraggable, false, bar, Math.max(1, spanCapped))}
            </td>
          );
          d += bar.span;
        }

      } else if (block) {
        const to    = new Date(block.dateTo);
        const toDay = (to.getFullYear() === year && to.getMonth() + 1 === month) ? to.getDate() : daysInMonth;
        const span  = Math.max(1, Math.min(toDay, daysInMonth) - currentDay + 1);
        const BLOCK_TYPE = BLOCK_TYPES.find(b => b.key === block.type) || BLOCK_TYPES[2];
        cells.push(
          <td key={`bl-${currentDay}`} colSpan={span}
            className="cal-td-booking"
            style={{ background: block.color + "22", cursor: "pointer" }}
            onClick={e => { e.stopPropagation(); if (selectMode) return; setEditBlock(block); setCellPopup(null); }}
          >
            <div className="cal-bar" style={{ borderLeft: `4px solid ${block.color}` }}>
              <span className="cal-bar-label" style={{ color: block.color }}>
                {BLOCK_TYPE.icon} {block.type === "Other" && block.note ? block.note : (block.type === "Occupied" ? t(lang, "calOccupied") : block.type === "Maintenance" ? t(lang, "calMaintenance") : t(lang, "calOther"))}
              </span>
            </div>
          </td>
        );
        d += span;

      } else {
        const spillover = unitBookings.find(b => {
          const bar = getBarProps(b);
          return bar && bar.startDay < currentDay && currentDay <= bar.endDay;
        });
        if (!spillover) {
          /* กล่องที่อยู่ในช่วงร่างการจอง (เฉพาะห้องที่ติ๊กไว้) — ระบายสีเป็นแถบร่าง
             ทำที่ระดับกล่องรายวัน ไม่ยุบเป็น colSpan เดียว เพื่อไม่ให้กระทบ layout เดิม */
          const rowDraft    = rowDraftRange;
          const inDraft     = !!rowDraft
            && currentDay >= rowDraft.startDay && currentDay <= rowDraft.endDay;
          const draftBad    = inDraft && !canPickUnit(unit._id);
          const isDraftStart = inDraft && currentDay === handleFirst;
          const isDraftEnd   = inDraft && currentDay === handleLast;

          cells.push(
            <td key={`e-${currentDay}`}
              className={`cal-td ${days[currentDay-1]?.isWeekend ? "weekend-cell" : ""} ${days[currentDay-1]?.isToday ? "today-cell" : ""}`
                + (inDraft ? ` cal-td-draft${draftBad ? " bad" : ""}` : "")
                + (isDraftStart ? " draft-start" : "") + (isDraftEnd ? " draft-end" : "")}
              onMouseDown={selectMode ? e => handleSelMouseDown(e, unit, currentDay) : undefined}
              onClick={e => handleCellClick(e, unit, currentDay)}
              style={{ cursor: selectMode ? "col-resize" : "cell" }}
            >
              {isDraftStart && (
                <div className="cal-draft-handle left"
                  onMouseDown={e => handleDraftResize(e, unit, "start", handleFirst)} />
              )}
              {isDraftEnd && (
                <div className="cal-draft-handle right"
                  onMouseDown={e => handleDraftResize(e, unit, "end", handleLast)} />
              )}
            </td>
          );
        }
        d++;
      }
    }
    return cells;
  };

  /* ============================================================
     RENDER DAY CELL (มุมมองรายวัน — 1 ห้อง 1 เซลล์กว้างเต็มจอ)
     ============================================================ */
  const renderDayCell = (unit) => {
    const unitBookings = getBookingsForUnit(unit._id);
    const unitBlocks   = localBlocks.filter(b => b.unitId?.toString() === unit._id?.toString());

    // การจองที่ครอบวันที่เลือก (getBarProps clamp เข้าเดือนนี้แล้ว)
    const booking = unitBookings.find(b => {
      const bar = getBarProps(b);
      return bar && bar.startDay <= selectedDay && selectedDay <= bar.endDay;
    });

    if (booking) {
      const cfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG.Confirmed;
      const guestName = booking.user
        ? `${booking.user.firstname ?? ""} ${booking.user.lastname ?? ""}`.trim() || booking.user.email || booking.guestName || "—"
        : booking.guestName || "—";
      const nights = Math.max(1, Math.round(
        (new Date(booking.checkOut) - new Date(booking.checkIn)) / 86400000
      ));
      const phone = booking.user?.phone || booking.guestInfo?.phone || "";
      const total = (booking.totalAmount || 0) + (booking.extraTotal || 0);
      const due   = Math.max(0, total - (booking.paidAmount || 0));
      const money = n => `฿${Number(n).toLocaleString()}`;

      return (
        <td className="cal-td-booking cal-td-dayview" style={{ background: cfg.bg, cursor: "pointer" }}
          onClick={e => handleBookingClick(e, booking, unit)}
          onMouseEnter={e => setTooltip({ x: e.clientX, y: e.clientY, booking })}
          onMouseLeave={() => setTooltip(null)}>
          <div className="cal-bar" style={{ borderLeft: `4px solid ${cfg.border}` }}>
            <span className="cal-bar-label" style={{ color: cfg.color, flexShrink: 0 }}>
              {statusLabel(booking.status)}
            </span>
            <span className="cal-bar-guest"
              style={{ color: cfg.color, fontSize: 11, fontWeight: 600, opacity: 0.85,
                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
              {guestName}
            </span>
            {booking.isWalkIn && <span className="cal-bar-tag">{t(lang, "calWalkIn")}</span>}
            {booking.roomType?.name && <span className="cal-bar-meta">{booking.roomType.name}</span>}
            {phone && <span className="cal-bar-meta">☎ {phone}</span>}
            <span className="cal-bar-dates">
              {new Date(booking.checkIn).toLocaleDateString("th-TH")} – {new Date(booking.checkOut).toLocaleDateString("th-TH")}
              {" · "}{nights} {t(lang, "calNightsUnit")}
            </span>
            {total > 0 && <span className="cal-bar-amount">{money(total)}</span>}
            {total > 0 && (
              due > 0
                ? <span className="cal-bar-due">{t(lang, "calDue")} {money(due)}</span>
                : <span className="cal-bar-paid">✓ {t(lang, "calPaidFull")}</span>
            )}
          </div>
        </td>
      );
    }

    // บล็อคที่ครอบวันที่เลือก — clamp แบบเดียวกับ month view (รวมวัน dateTo)
    const block = unitBlocks.find(b => {
      const from = new Date(b.dateFrom), to = new Date(b.dateTo);
      const fromDay = (from.getFullYear() === year && from.getMonth() + 1 === month)
        ? from.getDate() : (from < new Date(year, month - 1, 1) ? 1 : Infinity);
      const toDay = (to.getFullYear() === year && to.getMonth() + 1 === month)
        ? to.getDate() : (to >= new Date(year, month, 1) ? daysInMonth : -Infinity);
      return fromDay <= selectedDay && selectedDay <= toDay;
    });

    if (block) {
      const BLOCK_TYPE = BLOCK_TYPES.find(b => b.key === block.type) || BLOCK_TYPES[2];
      return (
        <td className="cal-td-booking cal-td-dayview"
          style={{ background: block.color + "22", cursor: "pointer" }}
          onClick={e => { e.stopPropagation(); if (selectMode) return; setEditBlock(block); setCellPopup(null); }}
        >
          <div className="cal-bar" style={{ borderLeft: `4px solid ${block.color}` }}>
            <span className="cal-bar-label" style={{ color: block.color }}>
              {BLOCK_TYPE.icon} {block.type === "Other" && block.note ? block.note : (block.type === "Occupied" ? t(lang, "calOccupied") : block.type === "Maintenance" ? t(lang, "calMaintenance") : t(lang, "calOther"))}
            </span>
            <span className="cal-bar-dates">
              {new Date(block.dateFrom).toLocaleDateString("th-TH")} – {new Date(block.dateTo).toLocaleDateString("th-TH")}
            </span>
          </div>
        </td>
      );
    }

    // ว่าง — คลิกเพื่อเพิ่มบล็อคของวันนี้
    const dayInfo = days[selectedDay - 1];
    return (
      <td className={`cal-td ${dayInfo?.isWeekend ? "weekend-cell" : ""} ${dayInfo?.isToday ? "today-cell" : ""}`}
        onClick={e => handleCellClick(e, unit, selectedDay)}
        style={{ cursor: "cell" }}
      />
    );
  };

  /* ============================================================
     JSX
     ============================================================ */
  return (
    <>
      {/* NAVBAR — แสดงเฉพาะ admin (staff ใช้ StaffLayout render ให้แล้ว) */}
      {role === "admin" && <NavbarAdmin />}

    <div className={`cal-page${selectMode ? " cal-select-mode" : ""}`}
      style={{ userSelect: (dragId || selectMode) ? "none" : "auto" }}>

      <ConfirmModal
        info={confirmInfo}
        onConfirm={handleConfirmSave}
        onCancel={handleConfirmCancel}
        lang={lang}
      />

      <BookingActionPopup
        info={bookingPopup}
        onClose={() => setBookingPopup(null)}
        onCancelBooking={handleCancelBooking}
        onNextStep={handleNextStep}
        busy={cancelling}
        statusLabel={statusLabel}
        lang={lang}
      />

      <CellActionPopup
        info={cellPopup}
        editBlock={editBlock}
        onClose={() => { setCellPopup(null); setEditBlock(null); }}
        onSave={handleCellSave}
        onCreateBooking={handleCreateBooking}
        onDelete={handleDelete}
        isUnitFree={isUnitFree}
        lang={lang}
      />

      {/* SIDEBAR */}
      <aside className="cal-sidebar">
        <div className="cal-date-badge">
          {`${String(new Date().getDate()).padStart(2,"0")} ${MONTHS_TH[new Date().getMonth()]} ${new Date().getFullYear() + (lang === "th" ? 543 : 0)}`}
        </div>

        <div className="cal-filter-group">
          <p className="cal-filter-title">{t(lang, "calRoomType")}</p>
          {roomTypes.map(rt => (
            <label key={rt} className={`cal-filter-item ${filterType === rt ? "active" : ""}`}
              onClick={() => setFilterType(rt)}>
              <span className="cal-dot" style={{ background: rt === "ทั้งหมด" ? "#6b7280" : (typeColorByName[rt] || "#1a237e") }} />
              {rt === "ทั้งหมด" ? t(lang, "calAllRooms") : rt}
            </label>
          ))}
        </div>

        <div className="cal-filter-group">
          <p className="cal-filter-title">{t(lang, "calRoomStatus")}</p>
          {[
            { key: "ทั้งหมด",                 labelKey: "calAllRooms",    color: "#6b7280" },
            { key: "ห้องที่ถูกจองแล้ว",       labelKey: "calBookedRooms", color: "#10b981" },
            { key: "ห้องที่ลูกค้าเข้าพักอยู่", labelKey: "calOccupiedRooms", color: "#3b82f6" },
            { key: "ห้องที่กำลังซ่อมแซม",     labelKey: "calMaintRooms",  color: "#ef4444" },
          ].map(({ key, labelKey, color }) => (
            <label key={key} className={`cal-filter-item ${filterStatus === key ? "active" : ""}`}
              onClick={() => setFilterStatus(key)}>
              <span className="cal-dot" style={{ background: color }} />
              {t(lang, labelKey)}
            </label>
          ))}
        </div>
      </aside>

      {/* MAIN */}
      <main className="cal-main">
        <div className="cal-topbar">
          <div className="cal-title-row">
            <span className="cal-icon">📅</span>
            <h2 className="cal-title">{t(lang, "calManageRooms")}</h2>

            {/* ZOOM CONTROLS — ซ่อนใน day view */}
            {!isDayView && (
            <div className="cal-zoom-group">
              <button
                className="cal-zoom-btn"
                onClick={zoomOut}
                disabled={zoomIdx === 0}
                title={t(lang, "calZoomOut")}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7.5"/>
                  <line x1="7.5" y1="11" x2="14.5" y2="11"/>
                  <line x1="17" y1="17" x2="22" y2="22"/>
                </svg>
              </button>
              <span className="cal-zoom-label">{Math.round((colWidth / ZOOM_LEVELS[ZOOM_LEVELS.length - 1]) * 100)}%</span>
              <button
                className="cal-zoom-btn"
                onClick={zoomIn}
                disabled={zoomIdx === ZOOM_LEVELS.length - 1}
                title={t(lang, "calZoomIn")}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7.5"/>
                  <line x1="11" y1="7.5" x2="11" y2="14.5"/>
                  <line x1="7.5" y1="11" x2="14.5" y2="11"/>
                  <line x1="17" y1="17" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            )}

            {/* จองหลายห้องพร้อมกัน — เปิดโหมดติ๊กเลือกห้อง */}
            <button
              className={`cal-multi-btn${selectMode ? " active" : ""}`}
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            >
              🛏️ {selectMode ? t(lang, "calMultiExit") : t(lang, "calMultiStart")}
            </button>

            {/* กลับจาก day view ไปดูทั้งเดือน */}
            {isDayView && (
              <button className="cal-back-btn" onClick={closeDayView}>
                ← {t(lang, "calBackToMonth")}
              </button>
            )}
          </div>
          <div className="cal-controls">
            <div className="cal-control-group">
              <span className="cal-control-label">{t(lang, "calMonth")}</span>
              <div className="cal-select-wrap">
                <select value={month} onChange={e => setMonth(Number(e.target.value))}>
                  {MONTHS_TH.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <span className="cal-chevron">▾</span>
              </div>
            </div>
            <div className="cal-control-group">
              <span className="cal-control-label">{t(lang, "calYear")}</span>
              <div className="cal-select-wrap">
                <select value={year} onChange={e => setYear(Number(e.target.value))}>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i)
                    .map(y => <option key={y} value={y}>{y + 543}</option>)}
                </select>
                <span className="cal-chevron">▾</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── แถบโหมดเลือกหลายห้อง ── */}
        {selectMode && (
          <div className="cal-multi-bar">
            <div className="cal-multi-dates">
              <span className="cal-multi-label">
                {selUnitIds.length ? t(lang, "calMultiSetAll") : t(lang, "calCheckIn")}
              </span>
              <DateField lang={lang} value={selIn}
                onChange={e => {
                  const v = e.target.value;
                  const nextOut = (v && selOut && v >= selOut) ? addDaysStr(v, 1) : selOut;
                  applyRangeToAll(v, nextOut);
                }} />
              <span className="cal-multi-arrow">→</span>
              <DateField lang={lang} value={selOut} min={selIn ? addDaysStr(selIn, 1) : undefined}
                onChange={e => applyRangeToAll(selIn, e.target.value)} />
              <span className="cal-multi-nights">
                {selRangeOk ? `${selNights} ${t(lang, "calNightsUnit")}` : `⚠️ ${t(lang, "calBkErrDate")}`}
              </span>
              {selUnitIds.length > 1 && !allSameRange && !hasConflict && (
                <span className="cal-multi-mixed">{t(lang, "calMultiMixedDates")}</span>
              )}
              {hasConflict && (
                <span className="cal-multi-conflict">
                  {t(lang, "calMultiConflict", conflictUnits.map(u => u.roomNumber).join(", "))}
                </span>
              )}
            </div>

            <div className="cal-multi-actions">
              <span className="cal-multi-count">
                {t(lang, "calMultiPicked", selUnitIds.length)}
                {selUnitIds.length > 0 && (
                  <b style={{ marginLeft: 8 }}>฿{selTotal.toLocaleString("th-TH")}</b>
                )}
              </span>
              <button className="cal-multi-ghost" onClick={selectAllFree} disabled={!selRangeOk}>
                {t(lang, "calMultiSelectAll")}
              </button>
              <button className="cal-multi-ghost" onClick={clearSelection} disabled={!selUnitIds.length}>
                {t(lang, "calMultiClear")}
              </button>
              <button className="cal-multi-go" onClick={openGroupBookingPopup}
                disabled={!selUnitIds.length || hasConflict}
                title={hasConflict ? t(lang, "calMultiConflict", conflictUnits.map(u => u.roomNumber).join(", ")) : ""}>
                {t(lang, "calMultiBook", selUnitIds.length)}
              </button>
            </div>
          </div>
        )}

        <div className="cal-scroll">
          <table className="cal-table" style={{ width:"100%", tableLayout:"fixed" }}>
            <colgroup>
              <col style={{ width: 88 }} />
              {isDayView ? (
                <col />
              ) : (
                days.map(({ day }) => (
                  <col key={day} style={{ width: colWidth }} />
                ))
              )}
            </colgroup>
            <thead>
              <tr>
                <th className="cal-th-room">
                  <span className="cal-th-day-lbl">{t(lang, "calDayColHead")}</span>
                  <span className="cal-th-room-lbl">{t(lang, "calRoomColHead")}</span>
                </th>
                {isDayView ? (
                  <th
                    className={`cal-th-day cal-th-dayview ${days[selectedDay - 1]?.isToday ? "today-head" : ""}`}
                    onMouseEnter={e => setDayTooltip({ day: selectedDay, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setDayTooltip(null)}
                  >
                    <button className="cal-daynav-btn" onClick={() => stepDay(-1)} title={t(lang, "calPrevDay")}>‹</button>
                    <span className="cal-dayview-title">
                      {days[selectedDay - 1]?.dow} {selectedDay} {MONTHS_TH[month - 1]} {year + 543}
                    </span>
                    <button className="cal-daynav-btn" onClick={() => stepDay(1)} title={t(lang, "calNextDay")}>›</button>
                  </th>
                ) : (
                  days.map(({ day, dow, isWeekend, isToday }) => (
                    <th
                      key={day}
                      className={`cal-th-day clickable ${isWeekend ? "weekend" : ""} ${isToday ? "today-head" : ""}`}
                      onMouseEnter={e => setDayTooltip({ day, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setDayTooltip(null)}
                      onClick={() => openDayView(day)}
                      title={t(lang, "calDayViewHint")}
                    >
                      <span className="cal-dow">{dow}</span>
                      <span className="cal-daynum">{day}</span>
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {filteredUnits.length === 0 ? (
                <tr><td colSpan={isDayView ? 2 : daysInMonth+1} className="cal-empty">{t(lang, "calNoRooms")}</td></tr>
              ) : (
                filteredUnits.map(unit => {
                  const uid      = unit._id?.toString();
                  const pickable = selectMode && canPickUnit(unit._id);
                  const picked   = selectMode && selUnitIds.includes(uid);
                  /* เลือกไว้แล้วแต่ช่วงวันไปทับการจองอื่น = โชว์แดง ไม่ใช่ทำจางหรือปลดออก */
                  const conflict = picked && !pickable;
                  return (
                  <tr key={unit._id}
                    className={`cal-row${selectMode && !pickable && !picked ? " cal-row-dim" : ""}`
                      + (picked ? " cal-row-picked" : "") + (conflict ? " cal-row-conflict" : "")}
                    data-unit-id={uid}>
                    <td className="cal-td-room" title={unit.roomType?.name || ""}>
                      {selectMode && (
                        <input
                          type="checkbox"
                          className="cal-room-check"
                          checked={picked}
                          /* ห้องที่ติ๊กไว้แล้วต้องเอาติ๊กออกได้เสมอ แม้ตอนนี้จะทับอยู่ */
                          disabled={!pickable && !picked}
                          title={pickable ? "" : t(lang, "calBkRoomBusy")}
                          onChange={() => toggleUnit(unit._id)}
                        />
                      )}
                      <span className="cal-room-num">{unit.roomNumber}</span>
                      <span className="cal-room-type">
                        {TYPE_SHORT_KEYS[unit.roomType?.type] ? t(lang, TYPE_SHORT_KEYS[unit.roomType?.type]) : ""}
                      </span>
                    </td>
                    {isDayView ? renderDayCell(unit) : renderCells(unit)}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* BOOKING BAR TOOLTIP */}
      {tooltip && !dragId && (
        <div className="cal-tooltip" style={getTooltipStyle(tooltip.x, tooltip.y)}>
          <div className="cal-tooltip-row">
            <span>{t(lang, "calGuestName")}</span>
            <strong>
              {tooltip.booking.user
                ? `${tooltip.booking.user.firstname ?? ""} ${tooltip.booking.user.lastname ?? ""}`.trim() || tooltip.booking.user.email || "—"
                : tooltip.booking.guestName || "—"}
            </strong>
          </div>
          <div className="cal-tooltip-row">
            <span>{t(lang, "calStatusLabel")}</span>
            <strong style={{ color: (STATUS_CONFIG[tooltip.booking.status] || STATUS_CONFIG.Confirmed).color }}>
              {statusLabel(tooltip.booking.status)}
            </strong>
          </div>
          <div className="cal-tooltip-row">
            <span>{t(lang, "calCheckIn")}</span>
            <strong>{new Date(tooltip.booking.checkIn).toLocaleDateString("th-TH")}</strong>
          </div>
          <div className="cal-tooltip-row">
            <span>{t(lang, "calCheckOut")}</span>
            <strong>{new Date(tooltip.booking.checkOut).toLocaleDateString("th-TH")}</strong>
          </div>
        </div>
      )}

      {/* DAY HEADER TOOLTIP */}
      {dayTooltip && !dragId && (
        <DayTooltip
          day={dayTooltip.day}
          month={month}
          year={year}
          units={units}
          bookings={bookings}
          blocks={localBlocks}
          style={getDayTooltipStyle(dayTooltip.x, dayTooltip.y)}
          lang={lang}
        />
      )}

      {/* TOAST */}
      <Toast show={!!toasting} type={toasting?.type} message={toasting?.msg} lang={lang} />

    </div>
    </>
  );
}

export default AdminCalendar;